/**
 * JobDetailModal — self-contained full-detail modal for a single job.
 *
 * Contains: ObservationRow, AddWorkItemModal, WorkItemCard, WorkItemMeasurementsPanel, ResourcePanel.
 * No imports from JobsPage — zero circular dependency risk.
 *
 * Editability has been relaxed across all stages: isEditable and isWorkItemEditable
 * are now both unconditionally true so the job detail panel can be modified before
 * work starts (CREATED / QUOTED / APPROVED), during work (IN_PROGRESS / PAUSED),
 * and after work has finished (COMPLETED / INVOICED / VEHICLE_COLLECTED / CANCELLED).
 * Backend authorisation rules remain the source of truth for what's actually
 * permitted.
 *
 * Measurements are rendered for ALL job statuses. The Customer Approvals tab is
 * a dedicated surface for job-level / batch approval flows. The Notes tab is
 * always enabled.
 *
 * UI relocation: the per-work-item measurement recording UI has been moved out of
 * WorkItemCard (Work Items tab) into JobNotesTab. JobNotesTab renders the measurement
 * panel for whichever work item is selected in its dropdown, via the exported
 * WorkItemMeasurementsPanel component. Behaviour is unchanged — same dispatches,
 * same state model — only the location in the modal differs.
 */

import { updateCustomerApi } from '../services/customerApi';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Wrench, X, XCircle, RefreshCw, AlertTriangle, CheckCircle, CheckCheck, Play, Pause, Car,
    UserPlus, Plus, Pencil, Trash2, Package, MapPin, Users, Boxes,
    Clock, Activity, Tag, Hash, Send, History, ChevronLeft, ChevronDown, ChevronUp, UserCheck, CalendarPlus,
    FileText } from 'lucide-react';
import {
    recordObservation,
    recordObservations,
    linkMeasurement,
    unlinkMeasurement,
    fetchAvailableMeasurements,
    addWorkItem,
    updateWorkItem,
    updateWorkItemStatus,
    removeWorkItem,
    startAllWorkItems,
    completeAllWorkItems,
    requestJobApproval,
    purgeJob,
    updateJobStatus,
    recordOdometerIn,
    scheduleJob,
    markJobVehicleReceived,
} from '../store/slices/jobSlice';
import JobNotesTab from './JobNotesTab';
import JobDetailLoading from './JobDetailLoading';
import RecordCrossLink from './RecordCrossLink';
import { invoiceDeepLink } from '../utils/recordLinks';
import { TransmissionBadge } from './TransmissionIcons';
import useEscapeToClose from './useEscapeToClose';
import RepcoImportModal from './RepcoImportModal';
import { openModal as openRepcoImportModal } from '../store/slices/repcoImportSlice';
import VehicleReceivedModal from './VehicleReceivedModal';
import { fetchLoanCarImageCategories } from '../store/slices/bookingSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

// Statuses available via the header status dropdown. Drives both the option
// list inside the JobDetailModal header pill and the labels shown when a
// job is dragged between kanban columns. Ordered to roughly follow the
// natural lifecycle so the dropdown reads top-to-bottom.
const JOB_STATUS_OPTIONS = [
    { value: 'CREATED',           label: 'Created' },
    { value: 'QUOTED',            label: 'Quoted' },
    { value: 'APPROVED',          label: 'Approved' },
    { value: 'IN_PROGRESS',       label: 'In Progress' },
    { value: 'PAUSED',            label: 'Paused' },
    { value: 'COMPLETED',         label: 'Completed' },
    { value: 'INVOICED',          label: 'Invoiced' },
    { value: 'VEHICLE_COLLECTED', label: 'Vehicle Collected' },
    { value: 'CANCELLED',         label: 'Cancelled' },
];

const STATUS_COLORS = {
    CREATED:           'bg-gray-100 text-gray-700',
    QUOTED:            'bg-blue-100 text-blue-700',
    APPROVED:          'bg-indigo-100 text-indigo-700',
    IN_PROGRESS:       'bg-violet-100 text-violet-700',
    PAUSED:            'bg-amber-100 text-amber-700',
    COMPLETED:         'bg-emerald-100 text-emerald-700',
    INVOICED:          'bg-teal-100 text-teal-700',
    CANCELLED:         'bg-red-100 text-red-700',
    VEHICLE_COLLECTED: 'bg-sky-100 text-sky-700',
};

// Saturated dot swatches for the header status picker — the pastel "bg-*-100"
// shades used on the pill itself read as grey at 8px dot size, so we keep a
// separate brighter swatch just for that affordance.
const STATUS_DOT_COLORS = {
    CREATED:           'bg-gray-400',
    QUOTED:            'bg-blue-500',
    APPROVED:          'bg-indigo-500',
    IN_PROGRESS:       'bg-violet-500',
    PAUSED:            'bg-amber-500',
    COMPLETED:         'bg-emerald-500',
    INVOICED:          'bg-teal-500',
    CANCELLED:         'bg-red-500',
    VEHICLE_COLLECTED: 'bg-sky-500',
};

const PRIORITY_COLORS = {
    URGENT: 'bg-red-100 text-red-700',
    HIGH:   'bg-orange-100 text-orange-700',
    NORMAL: 'bg-gray-100 text-gray-600',
    LOW:    'bg-blue-100 text-blue-600',
};

// ── ConfirmDialog ─────────────────────────────────────────────────────────────
//
// Reusable in-app confirmation dialog. Replaces window.confirm() so the prompt
// matches the rest of the app's visual language (rounded card, backdrop, themed
// buttons) and renders consistently on tablet — native browser dialogs look
// jarring on iPad and are easy to dismiss accidentally.
//
// Imperative-style API matching window.confirm: caller renders <ConfirmDialog/>
// with `open`, `onConfirm`, `onCancel` and the dialog handles its own backdrop
// + escape behaviour. `variant` switches the accent colour of the primary
// button — 'danger' (red) for destructive actions, 'warning' (amber) for
// reversible-but-risky ones.
const ConfirmDialog = ({
                           open,
                           title = 'Confirm',
                           message,
                           confirmLabel = 'Confirm',
                           cancelLabel = 'Cancel',
                           variant = 'danger',
                           onConfirm,
                           onCancel,
                       }) => {
    // Desktop-only Escape to close. Hook is called before the early return
    // so the Rules of Hooks invariant holds; the hook itself no-ops when
    // `open` is false.
    useEscapeToClose(open, onCancel);

    if (!open) return null;

    const variantStyles = {
        danger:  { btn: 'bg-red-600 hover:bg-red-700 text-white',       icon: 'bg-red-100 text-red-600',       iconCmp: <AlertTriangle size={18} /> },
        warning: { btn: 'bg-amber-500 hover:bg-amber-600 text-white',   icon: 'bg-amber-100 text-amber-600',   iconCmp: <AlertTriangle size={18} /> },
        primary: { btn: 'bg-violet-600 hover:bg-violet-700 text-white', icon: 'bg-violet-100 text-violet-600', iconCmp: <AlertTriangle size={18} /> },
    };
    const v = variantStyles[variant] || variantStyles.danger;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
            onClick={onCancel}
        >
            <div
                className="bg-white rounded-2xl w-full max-w-sm shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="px-5 py-4 border-b flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${v.icon}`}>
                        {v.iconCmp}
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                </div>
                <div className="px-5 py-4">
                    <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
                </div>
                <div className="px-5 py-3 border-t bg-gray-50 rounded-b-2xl flex gap-2 justify-end">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        onClick={onConfirm}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${v.btn}`}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Threshold / Observation helpers ──────────────────────────────────────────

const evaluateThreshold = (value, configJson) => {
    if (!value || !configJson) return 'unclassified';
    let thresholds;
    try { thresholds = JSON.parse(configJson); } catch { return 'unclassified'; }

    const tryNumeric = (val, expr) => {
        if (!expr) return false;
        expr = expr.trim();
        try {
            const n = parseFloat(val);
            if (isNaN(n)) return false;
            if (expr.startsWith('>=')) return n >= parseFloat(expr.slice(2));
            if (expr.startsWith('>'))  return n >  parseFloat(expr.slice(1));
            if (expr.startsWith('<=')) return n <= parseFloat(expr.slice(2));
            if (expr.startsWith('<'))  return n <  parseFloat(expr.slice(1));
            if (expr.includes('-')) {
                const [lo, hi] = expr.split('-').map(s => parseFloat(s));
                return n >= lo && n <= hi;
            }
            return n === parseFloat(expr);
        } catch { return false; }
    };

    const matches = (val, expr) => {
        if (!expr) return false;
        if (tryNumeric(val, expr)) return true;
        const v = val.trim().toLowerCase();
        const e = expr.trim().toLowerCase();
        return e.includes(v) || v.includes(e);
    };

    if (matches(value, thresholds.red))   return 'red';
    if (matches(value, thresholds.amber)) return 'amber';
    if (matches(value, thresholds.green)) return 'green';
    return 'unclassified';
};

const THRESHOLD_STYLES = {
    green:        { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', dot: 'bg-emerald-500', inputBg: 'bg-emerald-50', label: 'Good' },
    amber:        { bg: 'bg-amber-50',   border: 'border-amber-300',   text: 'text-amber-700',   dot: 'bg-amber-400',  inputBg: 'bg-amber-50',   label: 'Monitor' },
    red:          { bg: 'bg-red-50',     border: 'border-red-300',     text: 'text-red-700',     dot: 'bg-red-500',    inputBg: 'bg-red-50',     label: 'Action required' },
    unclassified: { bg: 'bg-white',      border: 'border-gray-200',    text: 'text-gray-400',    dot: 'bg-gray-300',   inputBg: 'bg-white',      label: '' },
};

/**
 * Sum a work item's cost detail: labour value + sum of part line values.
 * Null-safe at every level. Mirrors the backend WorkItemCostDetail shape
 * (labour.value : Number, parts[].value : Number).
 */
const workItemTotal = (item) => {
    if (!item || !item.costDetail) return 0;
    const labour = Number(item.costDetail.labour?.value) || 0;
    const parts = (item.costDetail.parts ?? []).reduce(
        (s, p) => s + (Number(p.value) || 0),
        0,
    );
    return labour + parts;
};

/**
 * Blanket rule: a work item is eligible to be sent for customer approval
 * only when it has a real dollar value AND the customer hasn't already
 * decided on it. Zero-cost inspection / check items (e.g. Repco
 * "Horn - Check/report") are workshop-only tasks and never go in an
 * approval request. Mirrors the server-side clamp in
 * RepcoImportService.commit so the UI and persisted state stay in sync.
 */
const isApprovable = (item) => {
    const undecided = item.customerConfirmed === null || item.customerConfirmed === undefined;
    return undecided && workItemTotal(item) > 0;
};

/**
 * Count work items in the given list that have at least one IN measurement
 * observation with no recorded value. Used by the customer-approval cards
 * (JobApprovalCard, BatchApprovalCard) to warn the mechanic before sending
 * a request when inspection data is still missing — the customer is being
 * asked to authorise work without seeing the IN-state of what's being
 * checked. The mechanic can still proceed if the situation is intentional
 * (e.g. quote based on a prior inspection report).
 *
 * Returns an array of { uuid, description, missingCount } objects, one per
 * item that has at least one blank IN observation. Items with no IN
 * observations defined are skipped (nothing to record).
 */
const findItemsMissingInValues = (items) =>
    (items || [])
        .map((wi) => {
            const inObs = wi.measurementObservations || [];
            const missingCount = inObs.filter((o) => !o.value || !String(o.value).trim()).length;
            return missingCount > 0
                ? { uuid: wi.uuid, description: wi.description, missingCount }
                : null;
        })
        .filter(Boolean);

const SLIDER_OPTIONS = [
    { band: 'red',   index: 0, icon: '✕', activeClass: 'bg-red-500 text-white border-red-500 scale-110 shadow-md',     inactiveClass: 'bg-white border-red-300 text-red-500 hover:scale-105',     hintClass: 'text-red-500'     },
    { band: 'amber', index: 1, icon: '⚠', activeClass: 'bg-amber-400 text-white border-amber-400 scale-110 shadow-md', inactiveClass: 'bg-white border-amber-300 text-amber-500 hover:scale-105', hintClass: 'text-amber-500'   },
    { band: 'green', index: 2, icon: '✓', activeClass: 'bg-emerald-500 text-white border-emerald-500 scale-110 shadow-md', inactiveClass: 'bg-white border-emerald-300 text-emerald-600 hover:scale-105', hintClass: 'text-emerald-600' },
];

const TRACK_COLORS   = { red: 'bg-red-400',   amber: 'bg-amber-400', green: 'bg-emerald-500' };
const TRACK_FILL_PCT = { red: '0%', amber: '50%', green: '100%' };

// ── SubCellValueInput ─────────────────────────────────────────────────────────
//
// One labelled text input for a single sub-position cell of a multi-dimensional
// measurement (e.g. tyre tread depth at LF OUTER, LF MIDDLE, LF INNER).
//
// Purely a VALUE recorder — does NOT show or set the green/amber/red band.
// The band for the whole position is set by the sibling PositionBandSlider; the
// per-cell text input just captures the distinct numeric reading for that
// sub-position. The cell still shows the band-derived border colour for visual
// continuity, but offers no UI to change the band — the slider is the only
// place band changes happen at the position level.
//
// Generic by design: works for any sub-position label (OUTER/MIDDLE/INNER for
// tyres, INNER/OUTER for brake pads, FRONT/REAR — whatever the measurement
// defines). Behaviour is identical regardless of the labels.
//
// Wire-up: parent supplies the observation, this component debounces and
// dispatches recordObservation for that one (measurementUuid, positionLabel,
// subPositionLabel) triple. The backend re-evaluates the threshold and the
// derived rollup picks up the new value automatically on the next refresh.

const SubCellValueInput = ({ obs, jobUuid, workItemUuid, isEditable, dispatch, phase = 'IN', onRecorded }) => {
    const [textValue, setTextValue] = useState(obs.value || '');
    const [saving,    setSaving]    = useState(false);
    const [saveError, setSaveError] = useState(null);
    const debounceRef = useRef(null);
    // Records the value we last sent to the backend so the post-save
    // round-trip can't stomp the user's text. Without this, a race between
    // the slice's re-fetch landing and `saving` flipping back to false can
    // cause the input to flicker back to the old value, making it look like
    // the save failed when it actually succeeded (the "bouncing back on
    // second attempt" symptom).
    const lastSavedRef = useRef(obs.value || '');

    // Reflect upstream changes only when (a) we're not mid-save, AND (b) the
    // new upstream value differs from what we last persisted. The second
    // check is the important one — it ignores the "echo" of our own save
    // coming back through the slice refresh, which is what was causing the
    // input to bounce.
    useEffect(() => {
        if (saving) return;
        const upstream = obs.value || '';
        if (upstream === lastSavedRef.current) return;
        setTextValue(upstream);
        lastSavedRef.current = upstream;
    }, [obs.value, saving]);

    // Border colour reflects the band the BACKEND classified for this cell.
    // The user can't change the band here (no slider) — they can only change
    // the value, which the backend then re-evaluates against the thresholds.
    const cellBand = (() => {
        const s = (obs.observationStatus || '').toLowerCase();
        if (s === 'green' || s === 'amber' || s === 'red') return s;
        if (obs.value) return evaluateThreshold(obs.value, obs.config);
        return 'unclassified';
    })();
    const style = THRESHOLD_STYLES[cellBand];

    const save = async (value) => {
        const trimmed = (value || '').trim();
        if (!trimmed) return; // backend rejects blank; cell stays as placeholder
        // Record the intent BEFORE the dispatch so the round-trip refresh
        // (which arrives via the obs prop) is recognised as our own echo
        // and doesn't trigger a textValue stomp via the sync effect.
        lastSavedRef.current = trimmed;
        setSaving(true); setSaveError(null);
        try {
            await dispatch(recordObservation({
                jobUuid,
                workItemUuid,
                measurementUuid:  obs.measurementUuid,
                value:            trimmed,
                positionLabel:    obs.positionLabel    || null,
                subPositionLabel: obs.subPositionLabel || null,
                phase,
            })).unwrap();
            if (onRecorded) onRecorded();
        } catch { setSaveError('Save failed'); }
        finally { setSaving(false); }
    };

    const handleChange = (e) => {
        const v = e.target.value;
        setTextValue(v); setSaveError(null);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        // Longer debounce than the standard observation row — these cells are
        // small and the user tabs between them; 1s avoids spamming the API
        // mid-walk-around-the-vehicle.
        debounceRef.current = setTimeout(() => save(v), 1000);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (debounceRef.current) clearTimeout(debounceRef.current);
            save(textValue);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            if (debounceRef.current) clearTimeout(debounceRef.current);
            setTextValue(obs.value || '');
            setSaveError(null);
        }
    };

    return (
        <div className="space-y-0.5">
            {/* Sub-position label — the cell's identity. Bold + uppercase so
                each of the N cells in a position is unmistakeably distinct. */}
            <div className="flex items-center justify-between gap-1">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    {obs.subPositionLabel || '—'}
                </span>
                {saving && <RefreshCw size={10} className="animate-spin text-violet-500 shrink-0" />}
            </div>

            <div className="relative">
                <input
                    type="text"
                    inputMode="decimal"
                    value={textValue}
                    onChange={handleChange}
                    onBlur={() => {
                        if (debounceRef.current) clearTimeout(debounceRef.current);
                        if ((textValue || '').trim() !== (obs.value || '').trim()) save(textValue);
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={!isEditable || saving}
                    placeholder={obs.unitSymbol ? `(${obs.unitSymbol})` : '—'}
                    className={`w-full px-2 py-1 pr-7 text-xs font-mono border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors ${style.border} ${style.inputBg}`}
                    aria-label={`${obs.positionLabel || ''} ${obs.subPositionLabel || ''}`.trim()}
                />
                {obs.unitSymbol && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-medium text-gray-400 pointer-events-none">
                        {obs.unitSymbol}
                    </span>
                )}
            </div>

            {saveError && (
                <p className="text-[9px] text-red-500 leading-tight">{saveError}</p>
            )}
        </div>
    );
};

// ── PositionBandSlider ────────────────────────────────────────────────────────
//
// Red/amber/green band selector for ONE position (e.g. LF) of a multi-dimensional
// measurement. Tapping a band writes the corresponding hint string (e.g. ">3mm"
// for green) to EVERY sub-cell observation in this position, in parallel. This
// is the "I just want to mark this tyre amber" quick-classify shortcut — useful
// when the technician has formed a judgement about the whole corner and doesn't
// want to record three identical values one-by-one.
//
// Subtle: typing a precise value into any SubCellValueInput afterwards
// overwrites that one cell, and the backend's rollup will recompute from the
// new mix of values. So the slider is the "broad strokes" tool; the cells are
// the "fine detail" tool. Both write to the same underlying observation rows.
//
// Generic: takes the array of observations for one position (any length, any
// sub-position labels) and dispatches to all of them. The bulk-write happens
// inside a single Promise.all so saved/error state stays consistent across
// cells.

const PositionBandSlider = ({ positionObservations, jobUuid, workItemUuid, isEditable, dispatch, phase = 'IN', onRecorded }) => {
    const [activeBand, setActiveBand] = useState(() => {
        // Initial band: prefer the backend's rollup status if it's set;
        // otherwise fall back to the worst per-cell evaluation locally.
        // Both produce the same answer in practice — the local eval is just
        // a safety net for payloads that pre-date positionRollupStatus.
        const rollup = (positionObservations[0]?.positionRollupStatus || '').toLowerCase();
        if (rollup === 'green' || rollup === 'amber' || rollup === 'red') return rollup;
        return null;
    });
    const [saving,    setSaving]    = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Sync local activeBand from upstream when not actively saving. Required
    // so the slider position updates after the user edits a sub-cell value
    // and the parent refetches the job.
    useEffect(() => {
        if (saving) return;
        const rollup = (positionObservations[0]?.positionRollupStatus || '').toLowerCase();
        if (rollup === 'green' || rollup === 'amber' || rollup === 'red') setActiveBand(rollup);
    }, [positionObservations, saving]);

    // Parse threshold hints from the first observation's config (every cell in
    // the group snapshots the same config so any of them works).
    const hints = (() => {
        try {
            const c = positionObservations[0]?.config ? JSON.parse(positionObservations[0].config) : null;
            return c || null;
        } catch { return null; }
    })();
    const bandCount = hints ? [hints.red, hints.amber, hints.green].filter(Boolean).length : 0;
    if (bandCount < 2) return null; // measurements with no usable thresholds get no slider

    const handleBandSelect = async (option) => {
        if (!isEditable || saving) return;
        const hint = hints?.[option.band] ?? option.band;

        // The band sets EVERY cell in the position, including ones that already
        // hold a value. Tapping green then red turns the whole position red.
        //
        // This replaced a fill-empty-only rule, which produced the confusing
        // result that prompted the change: green filled three blank cells, red
        // then had nothing to fill and left them green, and the position ended
        // up a mix of both bands with no error to explain it.
        //
        // ⚠️ It is destructive by decision. A technician who typed a real reading
        // — "3.2mm" at RR MIDDLE — loses it if the band is tapped afterwards.
        // That trade was made deliberately: the band is the fast path a mechanic
        // uses standing at the car, and a shortcut that silently does nothing on
        // some cells is worse than one that plainly overwrites.
        const targetObservations = positionObservations;

        const previousBand = activeBand;
        setActiveBand(option.band); // optimistic — revert on failure
        setSaveError(null);
        setSaving(true);
        try {
            // ⚠️ ONE request for all the empty cells, not Promise.all over N.
            //
            // Observations live in a JSON column, so each single-value write is a
            // read-modify-write of the whole list. Fired concurrently, all N read the
            // same original and the LAST one wins — tapping the green tick on a tyre
            // position filled one cell of three and silently dropped the other two,
            // with every request returning 200. The bulk endpoint applies them inside
            // one transaction, so they cannot lose each other.
            //
            await dispatch(recordObservations({
                jobUuid,
                workItemUuid,
                phase,
                observations: targetObservations.map(obs => ({
                    measurementUuid:  obs.measurementUuid,
                    value:            hint,
                    positionLabel:    obs.positionLabel    || null,
                    subPositionLabel: obs.subPositionLabel || null,
                })),
            })).unwrap();
            if (onRecorded) onRecorded();
        } catch {
            setSaveError('Save failed');
            setActiveBand(previousBand);
        } finally {
            setSaving(false);
        }
    };

    const activeIndex = SLIDER_OPTIONS.findIndex(o => o.band === activeBand);

    return (
        /* No dimmed state any more: the band always has something to do, because it
           now writes every cell rather than only the blank ones. */
        <div className="w-full">
            <div className="relative flex items-center">
                <div className="absolute inset-x-2 h-0.5 rounded-full bg-gray-200 top-2.5 z-0" />
                {activeIndex >= 0 && (
                    <div className={`absolute left-2 h-0.5 rounded-full top-2.5 z-0 transition-all duration-300 ${TRACK_COLORS[activeBand]}`}
                         style={{ width: TRACK_FILL_PCT[activeBand] }} />
                )}
                {SLIDER_OPTIONS.map(option => {
                    const isActive = option.band === activeBand;
                    return (
                        <button
                            key={option.band}
                            onClick={() => handleBandSelect(option)}
                            disabled={!isEditable || saving}
                            title={hints?.[option.band] ?? option.band}
                            className="relative z-10 flex-1 flex items-center justify-center focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition-all duration-200 ${isActive ? option.activeClass : option.inactiveClass}`}>
                                {option.icon}
                            </span>
                        </button>
                    );
                })}
            </div>
            {saveError && <p className="text-[9px] text-red-500 text-center mt-0.5">{saveError}</p>}
        </div>
    );
};

// ── ObservationRow ────────────────────────────────────────────────────────────
//
// Supports two render modes:
//   compact=false (default) — full-width row with measurement name header.
//                             Used for single-slot measurements.
//   compact=true            — narrow column card with position label badge only.
//                             Used inside multi-position grid (e.g. 4× tyre columns).
//
// The `phase` prop ('IN' | 'OUT') tells the row which observation list it represents.
// It's passed through to recordObservation so the backend writes to the correct list.
// The row body (slider/text input/styling) is identical regardless of phase — only
// the destination list changes.

const ObservationRow = ({ obs, jobUuid, workItemUuid, isEditable, dispatch, compact = false, phase = 'IN', onRecorded }) => {
    let hints = null;
    try { const c = obs.config ? JSON.parse(obs.config) : null; if (c) hints = c; } catch {}

    const bandCount = hints ? [hints.red, hints.amber, hints.green].filter(Boolean).length : 0;
    const useSlider = bandCount >= 2;

    const initialBand = (() => {
        const s = (obs.observationStatus || '').toLowerCase();
        if (s === 'green' || s === 'amber' || s === 'red') return s;
        if (obs.value) return evaluateThreshold(obs.value, obs.config);
        return null;
    })();

    const [activeBand, setActiveBand] = useState(initialBand);
    const [saving,     setSaving]     = useState(false);
    const [saveError,  setSaveError]  = useState(null);
    const [textValue,  setTextValue]  = useState(obs.value || '');
    const debounceRef = useRef(null);
    // Records the value we last sent to the backend so the post-save
    // round-trip can't stomp the local input. Without this, a tap on the
    // band slider followed by the slice's job re-fetch sometimes causes the
    // input to flicker back to its previous value (the "bouncing back"
    // symptom — looks like the save failed but it actually succeeded).
    const lastSavedRef = useRef(obs.value || '');

    useEffect(() => {
        // Skip the sync if the upstream value is our own save echoing back.
        // We still want to update the band classification in that case
        // (since the backend may have re-evaluated the threshold).
        const upstream = obs.value || '';
        const isOwnEcho = upstream === lastSavedRef.current;

        const s = (obs.observationStatus || '').toLowerCase();
        if (s === 'green' || s === 'amber' || s === 'red') {
            setActiveBand(s);
        } else if (obs.value) {
            setActiveBand(evaluateThreshold(obs.value, obs.config));
        } else {
            setActiveBand(null);
        }

        // Only re-sync the text input from upstream when it's NOT the echo
        // of our own most recent save. Otherwise the user-visible input can
        // briefly snap to a stale value while the round-trip resolves.
        if (!isOwnEcho) {
            setTextValue(upstream);
            lastSavedRef.current = upstream;
        }
    }, [obs.value, obs.observationStatus]);

    const style = THRESHOLD_STYLES[activeBand || 'unclassified'];

    const save = async (value) => {
        if (!value || !value.trim()) return;
        const trimmed = value.trim();
        // Record the intent BEFORE the dispatch so the round-trip refresh
        // arriving via the obs prop is recognised as our own echo and
        // doesn't trigger a textValue stomp via the sync effect.
        lastSavedRef.current = trimmed;
        setSaving(true); setSaveError(null);
        try {
            await dispatch(recordObservation({
                jobUuid,
                workItemUuid,
                measurementUuid: obs.measurementUuid,
                value: trimmed,
                positionLabel: obs.positionLabel || null,
                subPositionLabel: obs.subPositionLabel || null,
                phase,
            })).unwrap();
            if (onRecorded) onRecorded();
        } catch { setSaveError('Save failed'); }
        finally  { setSaving(false); }
    };

    const handleBandSelect = (option) => {
        if (!isEditable || saving) return;
        setActiveBand(option.band);
        setSaveError(null);
        const val = hints?.[option.band] ?? option.band;
        setTextValue(val);  // reflect the saved value in the text input
        save(val);
    };

    const handleTextChange = (e) => {
        const val = e.target.value;
        setTextValue(val); setSaveError(null);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => save(val), 800);
    };

    const activeIndex = SLIDER_OPTIONS.findIndex(o => o.band === activeBand);

    // ── Compact mode: multi-position column card ─────────────────────────
    // Shows both a text input for the actual measurement value (e.g. "3.2mm")
    // AND the slider for quick band selection. The tech can either type a value
    // (which the backend evaluates against thresholds) or tap a band directly.
    if (compact) {
        return (
            <div className={`rounded-lg border p-2 transition-all duration-200 ${style.bg} ${style.border} flex-1 min-w-0`}>
                {/* Position label + status */}
                <div className="flex items-center gap-1.5 mb-1.5 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${style.dot}`} />
                    <span className="text-xs font-bold text-gray-600 shrink-0">
                        {obs.positionLabel}
                        {obs.subPositionLabel && (
                            <span className="ml-1 px-1 py-0.5 bg-violet-100 text-violet-700 rounded text-[9px] font-bold uppercase tracking-wide">
                                {obs.subPositionLabel}
                            </span>
                        )}
                    </span>
                    {saving && <RefreshCw size={10} className="animate-spin text-gray-400 shrink-0 ml-auto" />}
                    {activeBand && activeBand !== 'unclassified' && (
                        <span className={`text-[10px] font-bold ml-auto truncate ${style.text}`} title={style.label}>{style.label}</span>
                    )}
                </div>

                {isEditable ? (
                    <div className="space-y-1.5">
                        {/* Value text input */}
                        <input
                            type="text"
                            value={textValue}
                            onChange={handleTextChange}
                            placeholder={obs.unitSymbol ? `Value (${obs.unitSymbol})` : 'Value…'}
                            className={`w-full border rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-violet-400 transition-colors ${style.inputBg} ${style.border}`}
                        />

                        {/* Slider — quick band selection */}
                        {useSlider && (
                            <div>
                                <div className="relative flex items-center">
                                    <div className="absolute inset-x-3 h-0.5 rounded-full bg-gray-200 top-3.5 z-0" />
                                    {activeIndex >= 0 && (
                                        <div className={`absolute left-3 h-0.5 rounded-full top-3.5 z-0 transition-all duration-300 ${TRACK_COLORS[activeBand]}`}
                                             style={{ width: TRACK_FILL_PCT[activeBand] }} />
                                    )}
                                    {SLIDER_OPTIONS.map(option => {
                                        const isActive = option.band === activeBand;
                                        return (
                                            <button key={option.band} onClick={() => handleBandSelect(option)} disabled={saving}
                                                    title={hints?.[option.band] ?? option.band}
                                                    className="relative z-10 flex-1 flex flex-col items-center gap-0.5 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed">
                                                <span className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all duration-200 ${isActive ? option.activeClass : option.inactiveClass}`}>
                                                    {option.icon}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {saveError && <p className="text-[10px] text-red-500 mt-0.5 text-center">{saveError}</p>}
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {/* Read-only value display */}
                        <div className={`px-2 py-1 rounded text-xs font-mono ${style.inputBg} border ${style.border}`}>
                            {obs.value || <span className="text-gray-400 italic">—</span>}
                        </div>

                        {/* Read-only slider — shows which band was recorded */}
                        {useSlider && activeBand && activeBand !== 'unclassified' && (
                            <div className="relative flex items-center">
                                <div className="absolute inset-x-3 h-0.5 rounded-full bg-gray-200 top-3.5 z-0" />
                                {activeIndex >= 0 && (
                                    <div className={`absolute left-3 h-0.5 rounded-full top-3.5 z-0 ${TRACK_COLORS[activeBand]}`}
                                         style={{ width: TRACK_FILL_PCT[activeBand] }} />
                                )}
                                {SLIDER_OPTIONS.map(option => {
                                    const isActive = option.band === activeBand;
                                    return (
                                        <div key={option.band}
                                             className="relative z-10 flex-1 flex flex-col items-center">
                                            <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold ${isActive ? option.activeClass : option.inactiveClass} opacity-${isActive ? '100' : '40'}`}>
                                                {option.icon}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // ── Standard mode: full-width row ────────────────────────────────────
    return (
        <div className={`rounded-lg border p-3 transition-all duration-200 ${style.bg} ${style.border}`}>
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
                <span className={`w-2 h-2 rounded-full shrink-0 transition-colors duration-200 ${style.dot}`} />
                <span className="text-xs font-semibold text-gray-700 flex-1 leading-tight">{obs.name}</span>
                {obs.positionLabel && <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded text-[10px] font-bold uppercase shrink-0">{obs.positionLabel}</span>}
                {obs.unitSymbol && <span className="text-xs text-gray-400 font-mono shrink-0">{obs.unitSymbol}</span>}
                {activeBand && activeBand !== 'unclassified' && <span className={`text-xs font-bold shrink-0 ${style.text}`}>{style.label}</span>}
                {saving && <RefreshCw size={11} className="animate-spin text-gray-400 shrink-0" />}
            </div>

            {/* Slider */}
            {isEditable && useSlider && (
                <div>
                    <div className="relative flex items-center">
                        <div className="absolute inset-x-5 h-1 rounded-full bg-gray-200 top-5 z-0" />
                        {activeIndex >= 0 && (
                            <div className={`absolute left-5 h-1 rounded-full top-5 z-0 transition-all duration-300 ${TRACK_COLORS[activeBand]}`}
                                 style={{ width: TRACK_FILL_PCT[activeBand] }} />
                        )}
                        {SLIDER_OPTIONS.map(option => {
                            const isActive = option.band === activeBand;
                            return (
                                <button key={option.band} onClick={() => handleBandSelect(option)} disabled={saving}
                                        title={hints?.[option.band] ?? option.band}
                                        className="relative z-10 flex-1 flex flex-col items-center gap-1 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed">
                                    <span className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-base font-bold transition-all duration-200 ${isActive ? option.activeClass : option.inactiveClass}`}>
                                        {option.icon}
                                    </span>
                                    <span className={`text-xs font-medium text-center leading-tight break-words w-full px-1 transition-colors ${isActive ? option.hintClass : 'text-gray-400'}`}>
                                        {hints?.[option.band] ?? option.band}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {saveError && <p className="text-xs text-red-500 mt-2 text-center">{saveError}</p>}
                </div>
            )}

            {/* Text input fallback */}
            {isEditable && !useSlider && (
                <div>
                    <input type="text" value={textValue} onChange={handleTextChange} placeholder="Enter value…"
                           className={`w-full border rounded-lg px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-400 transition-colors ${style.inputBg} ${style.border}`} />
                    {saveError && <p className="text-xs text-red-500 mt-1">{saveError}</p>}
                </div>
            )}

            {/* Read-only */}
            {!isEditable && (
                <div className={`px-3 py-1.5 rounded-lg text-sm font-mono ${style.inputBg} border ${style.border}`}>
                    {obs.value
                        || <span className="text-gray-400 italic">No value recorded</span>}
                </div>
            )}
        </div>
    );
};

// ── AddWorkItemModal ──────────────────────────────────────────────────────────

const WORK_ITEM_STATUS_TRANSITIONS = {
    PENDING:               ['IN_PROGRESS', 'AWAITING_CONFIRMATION', 'DECLINED'],
    AWAITING_CONFIRMATION: ['CONFIRMED', 'DECLINED'],
    CONFIRMED:             ['IN_PROGRESS'],
    IN_PROGRESS:           ['COMPLETED', 'AWAITING_CONFIRMATION'],
    COMPLETED:             [],
    DECLINED:              [],
};

const WORK_ITEM_STATUS_META = {
    PENDING:               { label: 'Pending',              color: 'bg-gray-100 text-gray-600'   },
    AWAITING_CONFIRMATION: { label: 'Awaiting Confirmation', color: 'bg-amber-100 text-amber-700' },
    CONFIRMED:             { label: 'Confirmed',             color: 'bg-sky-100 text-sky-700'     },
    IN_PROGRESS:           { label: 'In Progress',           color: 'bg-blue-100 text-blue-700'   },
    COMPLETED:             { label: 'Completed',             color: 'bg-emerald-100 text-emerald-700' },
    DECLINED:              { label: 'Declined',              color: 'bg-red-100 text-red-700'     },
};

const EMPTY_FORM = { description: '', detailNotes: '', workCategory: '', confirmationRequired: false, confirmationMessage: '', status: '', cdLabourValue: '', cdLabourDescription: '', cdParts: [] };
// Default part number to "N/A" — most workshop parts entered ad-hoc don't have a
// catalogue number on hand and techs were typing "N/A" themselves. Pre-seeding it
// removes that small papercut while still letting them overwrite when they do
// have a real part number.
const EMPTY_PART = { partNo: 'N/A', value: '', description: '' };

const buildCostDetail = (form) => {
    const hasLabour = form.cdLabourValue !== '' || form.cdLabourDescription !== '';
    // A part row counts as "filled" once the user has actually typed something —
    // value, description, or a partNo other than the default "N/A" placeholder.
    // This means clicking "Add Part" but leaving the row untouched won't force
    // validation or send an empty row to the server.
    const isPartFilled = (p) =>
        p.value !== '' || p.description !== '' || (p.partNo !== '' && p.partNo !== 'N/A');
    const hasParts = form.cdParts.some(isPartFilled);
    if (!hasLabour && !hasParts) return null;
    const detail = {};
    if (hasLabour) detail.labour = { value: form.cdLabourValue !== '' ? Number(form.cdLabourValue) : 0, description: form.cdLabourDescription.trim() };
    if (hasParts)  detail.parts  = form.cdParts.filter(isPartFilled).map(p => ({ partNo: p.partNo.trim() || 'N/A', value: p.value !== '' ? Number(p.value) : 0, description: p.description.trim() }));
    return detail;
};

const flattenCostDetail = (cd) => {
    if (!cd) return { cdLabourValue: '', cdLabourDescription: '', cdParts: [] };
    return {
        cdLabourValue:       cd.labour?.value       != null ? String(cd.labour.value) : '',
        cdLabourDescription: cd.labour?.description ?? '',
        cdParts:             (cd.parts ?? []).map(p => ({ partNo: p.partNo ?? '', value: p.value != null ? String(p.value) : '', description: p.description ?? '' })),
    };
};

const AddWorkItemModal = ({ job, onClose, onSubmit, loading, error, initialValues = null, mode = 'add', workCategories = [], categoryTypes = [] }) => {
    // Seed: a straight merge of EMPTY_FORM ← initialValues ← flattened cost
    // detail. The Work Item field is now a free-form text input, so there's
    // no picker-state to reverse-match against the category-types list — the
    // description is the only source of truth.
    const baseForm = initialValues
        ? { ...EMPTY_FORM, ...initialValues, ...flattenCostDetail(initialValues.costDetail) }
        : EMPTY_FORM;
    const [form, setForm]     = useState(baseForm);
    const [errors, setErrors] = useState({});

    // Desktop-only Escape to close.
    useEscapeToClose(true, onClose);

    const currentStatus = initialValues?.status || null;
    const isTerminal    = currentStatus && WORK_ITEM_STATUS_TRANSITIONS[currentStatus]?.length === 0;
    const nextOptions   = currentStatus ? WORK_ITEM_STATUS_TRANSITIONS[currentStatus] ?? [] : [];

    const set = (field, value) => { setForm(f => ({ ...f, [field]: value })); if (errors[field]) setErrors(e => ({ ...e, [field]: null })); };

    const validate = () => {
        const e = {};
        if (!form.description.trim()) e.description = 'Work item is required';
        else if (form.description.length > 500) e.description = 'Max 500 characters';
        if (form.detailNotes?.length > 2000) e.detailNotes = 'Max 2000 characters';
        if (form.confirmationRequired && !form.confirmationMessage?.trim()) e.confirmationMessage = 'Message is required when confirmation is enabled';
        if (form.confirmationMessage?.length > 1000) e.confirmationMessage = 'Max 1000 characters';
        const labourFilled = form.cdLabourValue !== '' || form.cdLabourDescription !== '';
        if (labourFilled) {
            if (form.cdLabourValue === '') e.cdLabourValue = 'Labour value is required';
            else if (isNaN(Number(form.cdLabourValue)) || Number(form.cdLabourValue) < 0) e.cdLabourValue = 'Must be a number ≥ 0';
            if (!form.cdLabourDescription.trim()) e.cdLabourDescription = 'Labour description is required';
            else if (form.cdLabourDescription.length > 500) e.cdLabourDescription = 'Max 500 characters';
        }
        form.cdParts.forEach((p, i) => {
            // Match buildCostDetail's "filled" rule — a row with only the default
            // "N/A" partNo (and nothing else) is still considered empty, so we
            // don't block submission with phantom validation errors.
            const pf = p.value !== '' || p.description !== '' || (p.partNo !== '' && p.partNo !== 'N/A');
            if (pf) {
                if (!p.partNo.trim()) e[`cdPart_${i}_partNo`] = 'Part number required';
                if (p.value === '') e[`cdPart_${i}_value`] = 'Value required';
                else if (isNaN(Number(p.value)) || Number(p.value) < 0) e[`cdPart_${i}_value`] = 'Must be ≥ 0';
                if (!p.description.trim()) e[`cdPart_${i}_description`] = 'Description required';
            }
        });
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const payload = { description: form.description.trim(), detailNotes: form.detailNotes?.trim() || null, workCategory: form.workCategory || null, confirmationRequired: form.confirmationRequired || false, confirmationMessage: form.confirmationRequired ? (form.confirmationMessage?.trim() || null) : null, costDetail: buildCostDetail(form) };
        if (mode === 'edit' && form.status && form.status !== currentStatus) payload.status = form.status;
        onSubmit(payload);
    };

    const addPart    = ()       => setForm(f => ({ ...f, cdParts: [...f.cdParts, { ...EMPTY_PART }] }));
    const removePart = (i)      => setForm(f => ({ ...f, cdParts: f.cdParts.filter((_, idx) => idx !== i) }));
    const setPart    = (i, k, v) => { setForm(f => ({ ...f, cdParts: f.cdParts.map((p, idx) => idx === i ? { ...p, [k]: v } : p) })); if (errors[`cdPart_${i}_${k}`]) setErrors(e => ({ ...e, [`cdPart_${i}_${k}`]: null })); };

    const inp = (field) => `w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200'}`;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col max-h-[92vh] shadow-2xl">
                <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-violet-100 rounded-xl flex items-center justify-center"><Tag size={16} className="text-violet-600" /></div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">{mode === 'edit' ? 'Edit Work Item' : 'Add Work Item'}</h2>
                            <p className="text-xs text-gray-400 mt-0.5">{job.jobNumber} · {job.customerName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X size={18} /></button>
                </div>

                <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
                    {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-xl"><AlertTriangle size={14} className="shrink-0" />{error}</div>}

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Work Item <span className="text-red-400">*</span></label>
                        {/*
                         * Free-form text input. Previously a searchable picker
                         * fed from /jobs/meta/category-types, but the workshop
                         * needs to be able to type anything (custom one-off
                         * items, unusual repairs, items not yet in the catalog)
                         * without being constrained to a fixed list. The picker
                         * is gone; the description field is the single source
                         * of truth. The same validate() rule still enforces
                         * non-empty and the 500-char ceiling.
                         */}
                        <input
                            type="text"
                            value={form.description}
                            onChange={e => set('description', e.target.value)}
                            placeholder="e.g. Front Brake Pad Replacement"
                            className={inp('description')}
                            maxLength={500}
                            autoFocus
                        />
                        {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
                    </div>

                    <div className={mode === 'edit' && currentStatus ? 'grid grid-cols-2 gap-3' : ''}>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Action</label>
                            <select
                                value={form.workCategory}
                                onChange={e => {
                                    const newCategory = e.target.value;
                                    // Work Category drives Labour Description: every change to the
                                    // category overwrites the description with the category's label.
                                    // Selecting "— None —" (empty value) clears the description.
                                    // This is intentional even when the tech has typed a custom
                                    // description — picking a category is treated as a fresh choice
                                    // and resets the description accordingly.
                                    setForm(f => {
                                        const next = { ...f, workCategory: newCategory };
                                        if (newCategory) {
                                            const matched = workCategories.find(t => t.value === newCategory);
                                            next.cdLabourDescription = matched?.label ?? '';
                                        } else {
                                            next.cdLabourDescription = '';
                                        }
                                        return next;
                                    });
                                    if (errors.workCategory) setErrors(e2 => ({ ...e2, workCategory: null }));
                                    if (errors.cdLabourDescription) setErrors(e2 => ({ ...e2, cdLabourDescription: null }));
                                }}
                                className={inp('workCategory') + ' bg-white'}
                            >
                                <option value="">— None —</option>
                                {workCategories.filter(t => t.label !== 'Not Supported').map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        {mode === 'edit' && currentStatus && (
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Status</label>
                                {isTerminal
                                    ? <div className="flex items-center h-[38px]"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${WORK_ITEM_STATUS_META[currentStatus]?.color}`}>{WORK_ITEM_STATUS_META[currentStatus]?.label ?? currentStatus}</span></div>
                                    : <select value={form.status} onChange={e => set('status', e.target.value)} className={inp('status') + ' bg-white'}>
                                        <option value="">{WORK_ITEM_STATUS_META[currentStatus]?.label ?? currentStatus} (current)</option>
                                        {nextOptions.map(s => <option key={s} value={s}>→ {WORK_ITEM_STATUS_META[s]?.label ?? s}</option>)}
                                    </select>
                                }
                            </div>
                        )}
                    </div>

                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-2">
                            <Hash size={13} className="text-violet-500" />
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Labour &amp; Parts</span>
                            {(form.cdLabourValue !== '' || form.cdParts.length > 0) && (
                                <span className="ml-auto px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded text-xs font-medium">
                                    {[form.cdLabourValue !== '' ? `labour $${Number(form.cdLabourValue || 0).toFixed(2)}` : null, form.cdParts.length > 0 ? `${form.cdParts.length} part${form.cdParts.length > 1 ? 's' : ''}` : null].filter(Boolean).join(' · ')}
                                </span>
                            )}
                        </div>
                        <div className="px-4 py-4 space-y-4 bg-white">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Wrench size={11} className="text-violet-400" /> Labour</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Value ($)</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                                            <input type="number" min="0" step="0.01" value={form.cdLabourValue} onChange={e => { set('cdLabourValue', e.target.value); if (errors.cdLabourValue) setErrors(ev => ({ ...ev, cdLabourValue: null })); }} placeholder="0.00" className={inp('cdLabourValue') + ' pl-6'} />
                                        </div>
                                        {errors.cdLabourValue && <p className="text-xs text-red-500 mt-1">{errors.cdLabourValue}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Description</label>
                                        <input type="text" value={form.cdLabourDescription} onChange={e => { set('cdLabourDescription', e.target.value); if (errors.cdLabourDescription) setErrors(ev => ({ ...ev, cdLabourDescription: null })); }} placeholder="e.g. Fit and bleed brakes" className={inp('cdLabourDescription')} />
                                        {errors.cdLabourDescription && <p className="text-xs text-red-500 mt-1">{errors.cdLabourDescription}</p>}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5"><Package size={11} className="text-violet-400" /> Parts</p>
                                    {form.cdParts.length < 50 && <button type="button" onClick={addPart} className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium px-2 py-1 rounded-lg hover:bg-violet-50 transition-colors"><Plus size={11} /> Add Part</button>}
                                </div>
                                {form.cdParts.length === 0
                                    ? <button type="button" onClick={addPart} className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-xs text-gray-400 hover:border-violet-300 hover:text-violet-500 transition-colors flex items-center justify-center gap-1.5"><Plus size={12} /> Add first part</button>
                                    : <div className="space-y-2">
                                        <div className="grid grid-cols-[1fr_80px_100px_24px] gap-2 px-1">
                                            <span className="text-xs text-gray-400">Description</span>
                                            <span className="text-xs text-gray-400">Value ($)</span>
                                            <span className="text-xs text-gray-400">Part No.</span>
                                            <span />
                                        </div>
                                        {form.cdParts.map((p, i) => (
                                            <div key={i} className="grid grid-cols-[1fr_80px_100px_24px] gap-2 items-start">
                                                <div>
                                                    <input type="text" value={p.description} onChange={e => setPart(i, 'description', e.target.value)} placeholder="Honda genuine oil filter" className={`w-full border rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-500 ${errors[`cdPart_${i}_description`] ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} />
                                                    {errors[`cdPart_${i}_description`] && <p className="text-xs text-red-500 mt-0.5">{errors[`cdPart_${i}_description`]}</p>}
                                                </div>
                                                <div>
                                                    <div className="relative">
                                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                                                        <input type="number" min="0" step="0.01" value={p.value} onChange={e => setPart(i, 'value', e.target.value)} placeholder="0.00" className={`w-full border rounded-lg pl-5 pr-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-500 ${errors[`cdPart_${i}_value`] ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} />
                                                    </div>
                                                    {errors[`cdPart_${i}_value`] && <p className="text-xs text-red-500 mt-0.5">{errors[`cdPart_${i}_value`]}</p>}
                                                </div>
                                                <div>
                                                    <input type="text" value={p.partNo} onChange={e => setPart(i, 'partNo', e.target.value)} placeholder="123ABC" className={`w-full border rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-violet-500 ${errors[`cdPart_${i}_partNo`] ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} />
                                                    {errors[`cdPart_${i}_partNo`] && <p className="text-xs text-red-500 mt-0.5">{errors[`cdPart_${i}_partNo`]}</p>}
                                                </div>
                                                <button type="button" onClick={() => removePart(i)} className="mt-1 p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Remove part"><X size={13} /></button>
                                            </div>
                                        ))}
                                        {form.cdParts.some(p => p.value !== '') && (
                                            <div className="flex justify-end pt-1 border-t border-dashed border-gray-100">
                                                <span className="text-xs text-gray-500 font-medium">Parts total: ${form.cdParts.reduce((s, p) => s + (p.value !== '' && !isNaN(Number(p.value)) ? Number(p.value) : 0), 0).toFixed(2)}</span>
                                            </div>
                                        )}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>

                    {/* Workshop Notes and per-work-item "Requires customer confirmation"
                        have been removed from this form. The work item form is now scoped
                        to identity (Description, Work Category) and cost (Labour & Parts)
                        only. Approval is handled via the Customer Approvals tab; internal
                        notes go on the job-level Notes tab. The detailNotes /
                        confirmationRequired / confirmationMessage fields are still sent
                        on the payload (empty / false / null) for backend compatibility. */}
                </div>

                <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex items-center gap-2 shrink-0">
                    <button onClick={onClose} className="px-4 py-2 border rounded-xl text-sm text-gray-500 hover:bg-white transition-colors">Cancel</button>
                    <button onClick={handleSubmit} disabled={loading} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors">
                        {loading ? <><RefreshCw size={14} className="animate-spin" /> Saving…</> : mode === 'edit' ? <><Pencil size={14} /> Save Changes</> : <><Plus size={14} /> Add Work Item</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── AddMeasurementPicker ──────────────────────────────────────────────────────

/**
 * Modal picker for linking a measurement from the org's catalogue
 * (org-owned + global) to an ad-hoc work item. After link succeeds the
 * ObservationRow renders immediately (placeholder value, UNCLASSIFIED status)
 * and the technician can record a value via the existing recordObservation flow.
 *
 * Caller is responsible for gating render on `!workItem.fromServiceProfile`.
 */
const AddMeasurementPicker = ({ jobUuid, workItem, onClose }) => {
    const dispatch = useDispatch();
    const {
        availableMeasurements = [],
        availableMeasurementsLoading,
        availableMeasurementsLoaded,
    } = useSelector((s) => s.jobs);

    const [search,     setSearch]     = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [linkError,  setLinkError]  = useState(null);
    // Measurement the user has tapped that would REPLACE the currently-linked
    // one. Null = no pending replacement. Holds the catalogue measurement so
    // the confirm dialog can name it.
    const [pendingReplace, setPendingReplace] = useState(null);

    // Desktop-only Escape to close.
    useEscapeToClose(true, onClose);

    // Lazy-load the catalogue the first time the picker is opened in the session
    useEffect(() => {
        if (!availableMeasurementsLoaded && !availableMeasurementsLoading) {
            dispatch(fetchAvailableMeasurements({}));
        }
    }, [dispatch, availableMeasurementsLoaded, availableMeasurementsLoading]);

    // A work item carries at most ONE measurement now (single-measurement rule).
    // Collect every distinct measurement currently linked across both phases so
    // a replace clears all of them (IN and OUT) before linking the new one.
    const linkedUuids = Array.from(new Set([
        ...(workItem?.measurementObservations    || []),
        ...(workItem?.measurementObservationsOut || []),
    ].map((o) => o.measurementUuid)));
    const linkedUuidSet = new Set(linkedUuids);
    // Friendly name of the currently-linked measurement for the confirm copy.
    const currentName = (workItem?.measurementObservations
        || workItem?.measurementObservationsOut || [])[0]?.name || 'the current measurement';

    const q = search.trim().toLowerCase();
    const filtered = (availableMeasurements || [])
        .filter((m) => !q
            || (m.name && m.name.toLowerCase().includes(q))
            || (m.measurementType && String(m.measurementType).toLowerCase().includes(q)));

    // Unlink every currently-linked measurement (clears IN + OUT observations),
    // then link the chosen one. Used both for the first link (no existing) and
    // for a confirmed replace.
    const linkMeasurementUuid = async (measurementUuid) => {
        setSubmitting(true);
        setLinkError(null);
        try {
            for (const existingUuid of linkedUuids) {
                if (existingUuid === measurementUuid) continue;
                await dispatch(unlinkMeasurement({
                    jobUuid,
                    workItemUuid: workItem.uuid,
                    measurementUuid: existingUuid,
                })).unwrap();
            }
            await dispatch(linkMeasurement({
                jobUuid,
                workItemUuid: workItem.uuid,
                measurementUuid,
            })).unwrap();
            onClose();
        } catch (err) {
            setLinkError(typeof err === 'string' ? err : 'Failed to add measurement');
            setSubmitting(false);
        }
    };

    const handlePick = (measurementUuid) => {
        // Tapping the already-linked measurement is a no-op — just close.
        if (linkedUuidSet.has(measurementUuid) && linkedUuids.length === 1) {
            onClose();
            return;
        }
        // If something else is already linked, confirm the destructive replace.
        if (linkedUuids.length > 0) {
            const m = (availableMeasurements || []).find((x) => x.uuid === measurementUuid);
            setPendingReplace(m || { uuid: measurementUuid, name: 'this measurement' });
            return;
        }
        // Nothing linked yet — straight link.
        linkMeasurementUuid(measurementUuid);
    };

    const handleConfirmReplace = () => {
        const uuid = pendingReplace?.uuid;
        setPendingReplace(null);
        if (uuid) linkMeasurementUuid(uuid);
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl w-full max-w-md flex flex-col max-h-[80vh] shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b flex items-center justify-between shrink-0">
                    <div className="min-w-0">
                        <h2 className="text-lg font-bold text-gray-900">
                            {linkedUuids.length > 0 ? 'Replace measurement' : 'Add measurement'}
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5 truncate">
                            {workItem?.description || 'Work item'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 shrink-0"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-4 border-b shrink-0">
                    <input
                        type="text"
                        placeholder="Search measurements…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    {linkError && <p className="text-xs text-red-600 mt-2">{linkError}</p>}
                </div>

                <div className="overflow-y-auto flex-1 px-2 py-2">
                    {availableMeasurementsLoading && (
                        <div className="py-8 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                            <RefreshCw size={14} className="animate-spin" />
                            Loading measurements…
                        </div>
                    )}

                    {!availableMeasurementsLoading && filtered.length === 0 && (
                        <div className="py-8 text-center text-sm text-gray-500">
                            {availableMeasurements.length === 0
                                ? 'No measurements have been defined yet. Add some in Settings → Measurements.'
                                : 'No measurements match your search.'}
                        </div>
                    )}

                    {filtered.map((m) => {
                        const isGlobal = m.organisationUuid == null && m.organisation == null;
                        const typeLabel = m.measurementTypeDisplay
                            || (m.measurementType ? String(m.measurementType) : null);
                        const isLinked = linkedUuidSet.has(m.uuid);
                        return (
                            <button
                                key={m.uuid}
                                onClick={() => handlePick(m.uuid)}
                                disabled={submitting}
                                className={`w-full text-left px-3 py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-start gap-3 group ${
                                    isLinked ? 'bg-violet-50 ring-1 ring-violet-200' : 'hover:bg-violet-50'
                                }`}
                            >
                                <Activity
                                    size={16}
                                    className="text-violet-500 mt-0.5 shrink-0 group-hover:text-violet-700"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                                        {m.name}
                                        {isLinked && (
                                            <span className="px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded text-[10px] font-semibold uppercase shrink-0">
                                                Linked
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                        {typeLabel && <span>{typeLabel}</span>}
                                        {isGlobal && (
                                            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium uppercase">
                                                Global
                                            </span>
                                        )}
                                        {m.positionLabels?.length > 0 && (
                                            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-medium">
                                                {m.positionLabels.join(' · ')}
                                                {m.subPositionLabels?.length > 0 && (
                                                    <span className="text-violet-500 ml-1">
                                                        × {m.subPositionLabels.join('/')}
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {!isLinked && (
                                    <Plus
                                        size={14}
                                        className="text-gray-300 group-hover:text-violet-600 mt-1 shrink-0"
                                    />
                                )}
                                {isLinked && (
                                    <CheckCircle
                                        size={14}
                                        className="text-violet-500 mt-1 shrink-0"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="px-5 py-3 border-t bg-gray-50 rounded-b-2xl shrink-0 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
                    >
                        Cancel
                    </button>
                </div>
            </div>

            {/* Replace confirmation — a work item holds only one measurement, so
                picking a different one removes the current measurement and every
                reading recorded against it (IN and OUT) before linking the new. */}
            <ConfirmDialog
                open={pendingReplace != null}
                title="Replace measurement?"
                message={`This will remove ${currentName} and any IN/OUT readings recorded against it for this work item, then link ${pendingReplace?.name || 'the new measurement'}. This cannot be undone.`}
                confirmLabel="Replace"
                cancelLabel="Cancel"
                variant="danger"
                onConfirm={handleConfirmReplace}
                onCancel={() => setPendingReplace(null)}
            />
        </div>
    );
};

// ── WorkItemCard ──────────────────────────────────────────────────────────────

const WorkItemCard = ({ item, job, isEditable, isWorkItemEditable, operationLoading, dispatch, onEdit, onRemove }) => {
    const [completingItem, setCompletingItem] = useState(false);
    // Which phase popup is open ('IN' | 'OUT' | null) and whether the
    // add-measurement picker is open. Only one measurement surface is shown at
    // a time (point 3) — opening the recorder or picker is mutually exclusive
    // through these two pieces of state living on the card.
    const [recorderPhase,        setRecorderPhase]        = useState(null);
    const [showMeasurementPicker, setShowMeasurementPicker] = useState(false);

    const canManageMeasurements = isEditable && !item.fromServiceProfile;

    // We only need to know whether ANY observations exist (in either phase)
    // so the card can show a small "this item has measurements" summary —
    // the actual recording UI (sliders, text inputs, IN/OUT toggle,
    // add/replace/delete) lives in the Notes tab now. Pairing measurement
    // recording with note-taking gave techs one clear "I'm recording state"
    // surface instead of two; the Work Items tab goes back to being a clean
    // "what needs doing" view.
    const observationsIn  = item.measurementObservations    || [];
    const observationsOut = item.measurementObservationsOut || [];
    const hasObservations = observationsIn.length > 0 || observationsOut.length > 0;

    const handleToggleComplete = async () => {
        const newStatus = item.status === 'COMPLETED' ? 'IN_PROGRESS' : 'COMPLETED';
        setCompletingItem(true);
        try {
            await dispatch(updateWorkItemStatus({
                jobUuid: job.uuid,
                workItemUuid: item.uuid,
                status: newStatus,
            })).unwrap();
        } catch {
            // operationError surfaces via the slice's toast
        } finally {
            setCompletingItem(false);
        }
    };

    return (
        <div className={`rounded-xl px-4 py-3 space-y-2 transition-colors ${item.status === 'COMPLETED' ? 'bg-emerald-50/50' : 'bg-gray-50'}`}>
            {/* Row 1: checkbox + description + badges + actions */}
            <div className="flex items-start gap-3">
                {isWorkItemEditable && (
                    <button
                        onClick={handleToggleComplete}
                        disabled={operationLoading || completingItem}
                        className="mt-0.5 shrink-0 group"
                        title={item.status === 'COMPLETED' ? 'Mark as in progress' : 'Mark as completed'}
                    >
                        {completingItem ? (
                            <RefreshCw size={16} className="animate-spin text-gray-400" />
                        ) : item.status === 'COMPLETED' ? (
                            <CheckCircle size={18} className="text-emerald-500 group-hover:text-emerald-700 transition-colors" />
                        ) : (
                            <div className="w-[18px] h-[18px] rounded-full border-2 border-gray-300 group-hover:border-emerald-400 group-hover:bg-emerald-50 transition-colors" />
                        )}
                    </button>
                )}
                {!isWorkItemEditable && item.status === 'COMPLETED' && (
                    <CheckCircle size={18} className="text-emerald-500 mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-medium ${item.status === 'COMPLETED' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{item.description}</p>
                        {item.fromServiceProfile && <span className="shrink-0 px-1.5 py-0.5 bg-violet-100 text-violet-600 rounded text-xs font-medium">Profile</span>}
                    </div>
                    {item.detailNotes && <p className="text-xs text-gray-500 mt-0.5">{item.detailNotes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {(() => {
                        const total = workItemTotal(item);
                        return total > 0 ? <span className="text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">${total.toFixed(2)}</span> : null;
                    })()}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        item.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700' :
                            item.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                                item.status === 'AWAITING_CONFIRMATION' ? 'bg-amber-100 text-amber-700' :
                                    item.status === 'DECLINED' ? 'bg-red-100 text-red-700' :
                                        item.status === 'CONFIRMED' ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-600'
                    }`}>{item.status?.replace(/_/g, ' ')}</span>
                    {isWorkItemEditable && <>
                        <button onClick={onEdit} disabled={operationLoading} className="p-1 text-gray-300 hover:text-violet-500 hover:bg-violet-50 rounded-lg transition-colors" title="Edit"><Pencil size={13} /></button>
                        <button onClick={onRemove} disabled={operationLoading} className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Remove"><Trash2 size={13} /></button>
                    </>}
                </div>
            </div>

            {/* Row 2: confirmation (only shown for WORK_ITEM approval mode) */}
            {job.approvalMode !== 'PROFILE' && item.confirmationRequired && (
                <div className="flex items-center gap-2 pt-1.5">
                    <span className="text-xs text-gray-400">Customer confirmation:</span>
                    {item.customerConfirmed === true  && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold"><CheckCircle size={10} /> Approved</span>}
                    {item.customerConfirmed === false && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold"><X size={10} /> Declined</span>}
                    {(item.customerConfirmed === null || item.customerConfirmed === undefined) && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 rounded-full text-xs font-semibold"><Clock size={10} /> Not approved by customer — send approval request</span>}
                    {item.confirmationMessage && <span className="text-xs text-gray-400 italic truncate max-w-[200px]" title={item.confirmationMessage}>"{item.confirmationMessage}"</span>}
                </div>
            )}

            {/* Row 3: cost detail */}
            {item.costDetail && (item.costDetail.labour || item.costDetail.parts?.length > 0) && (
                <div className="space-y-1 pt-1.5 border-t border-gray-200">
                    {item.costDetail.labour && (
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                            <Wrench size={10} className="text-violet-400 shrink-0" />
                            <span className="text-gray-500 font-medium min-w-[48px]">Labour</span>
                            <span className="text-gray-700 flex-1 truncate">{item.costDetail.labour.description}</span>
                            <span className="font-medium text-gray-700 shrink-0">${Number(item.costDetail.labour.value).toFixed(2)}</span>
                        </div>
                    )}
                    {item.costDetail.parts?.map((p, pi) => (
                        <div key={pi} className="flex items-center gap-2 text-xs text-gray-600">
                            <Package size={10} className="text-violet-400 shrink-0" />
                            <span className="font-mono text-gray-400 shrink-0 min-w-[60px] truncate" title={p.partNo}>{p.partNo}</span>
                            <span className="text-gray-700 flex-1 truncate">{p.description}</span>
                            <span className="font-medium text-gray-700 shrink-0">${Number(p.value).toFixed(2)}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Row 4: Measurements.
                The Work Items tab is now where measurements get recorded.
                • Big IN / OUT buttons (shown once at least one measurement is
                  linked) open a focused recorder popup for that phase — much
                  better for hand-held devices than the old Notes-tab panel.
                  Amber = that phase not yet fully recorded, green = complete.
                • "Add measurement" is always available (when editable) so a
                  tech can link a measurement to this item at any time.
                Only one measurement surface (recorder OR picker) is open at a
                time. */}
            {(hasObservations || canManageMeasurements) && (
                <div className="pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Activity size={14} className="text-violet-500 shrink-0" />
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Measurements</p>

                        {hasObservations && (
                            <div className="flex items-center gap-2">
                                {/* IN button */}
                                <button
                                    type="button"
                                    onClick={() => { setShowMeasurementPicker(false); setRecorderPhase('IN'); }}
                                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                                        item.measurementsCompleteIn
                                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                                            : 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                                    }`}
                                    title={item.measurementsCompleteIn ? 'IN readings complete — tap to review/edit' : 'Record IN (baseline) readings'}
                                >
                                    {item.measurementsCompleteIn
                                        ? <CheckCircle size={13} />
                                        : <AlertTriangle size={13} />}
                                    IN
                                </button>

                                {/* OUT button */}
                                <button
                                    type="button"
                                    onClick={() => { setShowMeasurementPicker(false); setRecorderPhase('OUT'); }}
                                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                                        item.measurementsCompleteOut
                                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                                            : 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                                    }`}
                                    title={item.measurementsCompleteOut ? 'OUT readings complete — tap to review/edit' : 'Record OUT (post-work) readings'}
                                >
                                    {item.measurementsCompleteOut
                                        ? <CheckCircle size={13} />
                                        : <AlertTriangle size={13} />}
                                    OUT
                                </button>
                            </div>
                        )}

                        <div className="flex-1" />

                        {/* Add measurement — always available when editable. */}
                        {canManageMeasurements && (
                            <button
                                type="button"
                                onClick={() => { setRecorderPhase(null); setShowMeasurementPicker(true); }}
                                disabled={operationLoading}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-violet-600 hover:text-white hover:bg-violet-600 border border-violet-200 hover:border-violet-600 rounded-lg transition-colors disabled:opacity-50"
                                title="Add a measurement to this work item"
                            >
                                <Plus size={13} /> Add measurement
                            </button>
                        )}
                    </div>

                    {!hasObservations && canManageMeasurements && (
                        <p className="text-[11px] text-gray-400 italic mt-1.5">
                            No measurements yet — tap <span className="font-semibold text-violet-600">Add measurement</span> to link one, then record IN / OUT readings.
                        </p>
                    )}
                </div>
            )}

            {/* Recorder popup — phase-locked, launched from the IN / OUT buttons. */}
            {recorderPhase && (
                <MeasurementRecorderModal
                    item={item}
                    job={job}
                    phase={recorderPhase}
                    isEditable={isEditable}
                    isWorkItemEditable={isWorkItemEditable}
                    operationLoading={operationLoading}
                    dispatch={dispatch}
                    onClose={() => setRecorderPhase(null)}
                />
            )}

            {/* Add-measurement picker — links a measurement from the catalogue. */}
            {showMeasurementPicker && (
                <AddMeasurementPicker
                    jobUuid={job.uuid}
                    workItem={item}
                    onClose={() => setShowMeasurementPicker(false)}
                />
            )}
        </div>
    );
};

// ── WorkItemMeasurementsPanel ─────────────────────────────────────────────────
//
// Stand-alone measurement recording UI for a single work item.
//
// Recording now happens inside a popup (MeasurementRecorderModal) launched from
// the big IN / OUT buttons on each WorkItemCard in the Work Items tab — see
// WorkItemCard. This is much better for hand-held devices: the tech taps IN (or
// OUT), gets a focused full-attention recorder, and the popup dismisses once
// readings are captured. The Notes tab no longer hosts measurement recording.
//
// Props:
//   item, job                 – the work item and its parent job
//   isEditable                – job-level edit gate
//   isWorkItemEditable        – work-item-level edit gate
//   operationLoading          – disable buttons during in-flight dispatches
//   dispatch                  – redux dispatch
//   lockedPhase               – 'IN' | 'OUT'. When set, the IN/OUT toggle is
//                                hidden and every observation row records into
//                                that phase. Driven by which big button was
//                                tapped on the card. When omitted, the legacy
//                                in-panel toggle is shown (kept for safety /
//                                any other caller).
//   onRecorded                – called after any successful observation save.
//                                The launcher (WorkItemCard) uses this to decide
//                                whether to auto-dismiss the popup.

const WorkItemMeasurementsPanel = ({ item, job, isEditable, isWorkItemEditable, operationLoading, dispatch, lockedPhase, onRecorded }) => {
    const [showMeasurementPicker, setShowMeasurementPicker] = useState(false);
    const [unlinkingUuid,         setUnlinkingUuid]         = useState(null);
    const [activePhase,           setActivePhase]           = useState(lockedPhase || 'IN');
    // UUID of the measurement pending unlink confirmation. Replaces the prior
    // window.confirm() so the prompt renders inside the app rather than as a
    // native browser dialog (which looks out of place, especially on iPad).
    const [pendingUnlinkUuid,     setPendingUnlinkUuid]     = useState(null);

    // When a phase is locked (popup launched from a specific IN / OUT button),
    // pin to it. Otherwise reset to IN whenever the parent switches work item.
    useEffect(() => { setActivePhase(lockedPhase || 'IN'); }, [item.uuid, lockedPhase]);

    const phaseLocked = lockedPhase === 'IN' || lockedPhase === 'OUT';
    const canManageMeasurements = isEditable && !item.fromServiceProfile;

    const observationsIn  = item.measurementObservations    || [];
    const observationsOut = item.measurementObservationsOut || [];
    const observations    = activePhase === 'OUT' ? observationsOut : observationsIn;
    const hasObservations = observationsIn.length > 0 || observationsOut.length > 0;

    const requestUnlink = (measurementUuid) => {
        if (!measurementUuid) return;
        setPendingUnlinkUuid(measurementUuid);
    };

    const performUnlink = async (measurementUuid) => {
        setUnlinkingUuid(measurementUuid);
        try {
            await dispatch(unlinkMeasurement({
                jobUuid: job.uuid,
                workItemUuid: item.uuid,
                measurementUuid,
            })).unwrap();
        } catch {
            // operationError surfaces via the slice's toast
        } finally {
            setUnlinkingUuid(null);
        }
    };

    const handleConfirmUnlink = () => {
        const uuid = pendingUnlinkUuid;
        setPendingUnlinkUuid(null);
        if (uuid) performUnlink(uuid);
    };

    if (!hasObservations && !canManageMeasurements) return null;

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
                <Activity size={12} className="text-violet-500 shrink-0" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Measurements</p>

                {/* IN/OUT phase toggle — only when NOT launched from a specific
                    IN / OUT button. The popup launched from the card locks the
                    phase, so this toggle is hidden there. */}
                {hasObservations && !phaseLocked && (
                    <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs font-semibold shrink-0">
                        <button
                            type="button"
                            onClick={() => setActivePhase('IN')}
                            className={`px-2.5 py-0.5 transition-colors ${
                                activePhase === 'IN'
                                    ? 'bg-violet-600 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                            title="Pre-work (baseline) readings"
                        >
                            IN
                        </button>
                        <button
                            type="button"
                            onClick={() => setActivePhase('OUT')}
                            className={`px-2.5 py-0.5 border-l border-gray-300 transition-colors ${
                                activePhase === 'OUT'
                                    ? 'bg-violet-600 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                            title="Post-work readings"
                        >
                            OUT
                        </button>
                    </div>
                )}

                {/* Per-measurement delete buttons live next to each rendered
                    group below. The header used to carry a single shortcut
                    "Delete" button, but with multi-measurement support there's
                    no obvious single target, so it has been removed. */}

                <div className="flex-1" />

                {/* Phase-aware completeness badges */}
                {hasObservations && (
                    <div className="flex items-center gap-1.5 shrink-0">
                        <span
                            className={`text-[10px] font-bold flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                item.measurementsCompleteIn
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-amber-100 text-amber-700'
                            }`}
                            title={item.measurementsCompleteIn ? 'IN readings complete' : 'IN readings incomplete'}
                        >
                            {item.measurementsCompleteIn
                                ? <CheckCircle size={10} />
                                : <AlertTriangle size={10} />}
                            IN
                        </span>
                        <span
                            className={`text-[10px] font-bold flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                item.measurementsCompleteOut
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-amber-100 text-amber-700'
                            }`}
                            title={item.measurementsCompleteOut ? 'OUT readings complete' : 'OUT readings incomplete'}
                        >
                            {item.measurementsCompleteOut
                                ? <CheckCircle size={10} />
                                : <AlertTriangle size={10} />}
                            OUT
                        </span>
                    </div>
                )}

                {/* In-panel Add — only when NOT inside the recorder popup. The
                    popup is launched per-phase from the card, which already has
                    its own "Add measurement" control, so a second Add here would
                    be redundant. */}
                {canManageMeasurements && !phaseLocked && (
                    <button
                        onClick={() => setShowMeasurementPicker(true)}
                        disabled={operationLoading}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-violet-600 hover:text-white hover:bg-violet-600 border border-violet-200 hover:border-violet-600 rounded-md transition-colors disabled:opacity-50"
                        title="Add a measurement to this work item"
                    >
                        <Plus size={11} /> Add
                    </button>
                )}
            </div>

            {hasObservations ? (() => {
                const groups = [];
                const seen = new Map();
                for (const obs of observations) {
                    if (!seen.has(obs.measurementUuid)) {
                        const group = { measurementUuid: obs.measurementUuid, name: obs.name, unitSymbol: obs.unitSymbol, items: [] };
                        seen.set(obs.measurementUuid, group);
                        groups.push(group);
                    }
                    seen.get(obs.measurementUuid).items.push(obs);
                }

                return groups.map(group => {
                    // Three shapes to render:
                    //   1. Single-slot                — full-width ObservationRow.
                    //   2. Positions only             — horizontal grid of compact cells.
                    //   3. Positions + sub-positions  — 2D grid: positions as rows,
                    //                                   sub-positions as columns,
                    //                                   per-position rollup badge derived
                    //                                   from positionRollupStatus.
                    //
                    // The detection happens row-by-row so a measurement that has
                    // only positionLabel rows still hits case 2; only when any
                    // row carries subPositionLabel do we switch to the nested layout.
                    const hasSubPositions = group.items.some(o => o.subPositionLabel);
                    const isMultiPosition = group.items.length > 1 && group.items.some(o => o.positionLabel);

                    // ── Case 3: 2D grid for sub-positioned measurements ────────
                    if (hasSubPositions) {
                        // Bucket rows by position to derive the visible row order.
                        // We don't sort — the backend returns rows in stable insertion
                        // order which matches the measurement's declared label order,
                        // so the user always sees LF/RF/LR/RR (or whatever was configured).
                        const positionBuckets = new Map();
                        const subPositionsSeen = new Set();
                        for (const obs of group.items) {
                            const pos = obs.positionLabel || '';
                            if (!positionBuckets.has(pos)) positionBuckets.set(pos, []);
                            positionBuckets.get(pos).push(obs);
                            if (obs.subPositionLabel) subPositionsSeen.add(obs.subPositionLabel);
                        }
                        // Preserve sub-position label order from the first position bucket —
                        // every position has the same set (the backend seeds the full grid).
                        const firstBucket = positionBuckets.values().next().value || [];
                        const subPositionOrder = firstBucket
                            .map(o => o.subPositionLabel)
                            .filter(Boolean);

                        return (
                            <div key={group.measurementUuid} className="relative group">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-xs font-semibold text-gray-700">{group.name}</span>
                                    {group.unitSymbol && <span className="text-xs text-gray-400 font-mono">{group.unitSymbol}</span>}
                                    <span className="text-[10px] text-gray-400">
                                        {positionBuckets.size} × {subPositionOrder.length} grid
                                    </span>
                                </div>

                                <div className="space-y-1.5">
                                    {Array.from(positionBuckets.entries()).map(([pos, posObs]) => {
                                        // Per-position rollup — every row in the bucket carries
                                        // the same positionRollupStatus, so picking the first
                                        // is enough. Fallback handles older payloads gracefully.
                                        const rollup = (posObs[0]?.positionRollupStatus || 'UNCLASSIFIED').toLowerCase();
                                        const rollupStyle = THRESHOLD_STYLES[rollup] || THRESHOLD_STYLES.unclassified;

                                        return (
                                            // One card per position. Layout (vertical stack):
                                            //
                                            //   ┌─────────────────────────────────────────────┐
                                            //   │ LF  • Good          [ ✕  ⚠  ✓ slider ]      │  ← compact header
                                            //   │ [ OUTER ]   [ MIDDLE ]   [ INNER ]          │  ← values
                                            //   └─────────────────────────────────────────────┘
                                            //
                                            // Header is a single line — label + rollup badge
                                            // inline on the left, slider right-aligned. Saves
                                            // ~16px of vertical space per row vs the stacked
                                            // header so a 4-position grid (tyres) fits comfortably
                                            // without scrolling on most viewports.
                                            <div key={pos}
                                                 className={`px-2 py-1.5 rounded-lg border transition-colors duration-200 space-y-1.5 ${rollupStyle.bg} ${rollupStyle.border}`}>
                                                {/* Compact header: label + rollup chip inline on
                                                    left, band slider on right. */}
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        <span className="text-xs font-bold text-gray-700 leading-none">{pos}</span>
                                                        {rollup !== 'unclassified' && (
                                                            <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold uppercase leading-none ${rollupStyle.text}`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${rollupStyle.dot}`} />
                                                                {rollupStyle.label}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="flex-1 max-w-[140px] ml-auto">
                                                        <PositionBandSlider
                                                            positionObservations={posObs}
                                                            jobUuid={job.uuid}
                                                            workItemUuid={item.uuid}
                                                            isEditable={isWorkItemEditable}
                                                            dispatch={dispatch}
                                                            phase={activePhase}
                                                            onRecorded={onRecorded}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Sub-position cells — plain labelled value inputs.
                                                    Full width below the header so each cell gets
                                                    enough room to type into comfortably. Map by
                                                    subPositionLabel so cells stay aligned even if
                                                    the backend ever returns rows in a different order. */}
                                                <div className={`grid gap-1.5 ${
                                                    subPositionOrder.length === 2 ? 'grid-cols-2' :
                                                        subPositionOrder.length === 3 ? 'grid-cols-3' :
                                                            subPositionOrder.length === 4 ? 'grid-cols-2 sm:grid-cols-4' :
                                                                'grid-cols-2 sm:grid-cols-3'
                                                }`}>
                                                    {subPositionOrder.map(sub => {
                                                        const obs = posObs.find(o => o.subPositionLabel === sub);
                                                        if (!obs) return <div key={sub} />;
                                                        return (
                                                            <SubCellValueInput
                                                                key={`${obs.measurementUuid}-${obs.positionLabel}-${obs.subPositionLabel}`}
                                                                obs={obs}
                                                                jobUuid={job.uuid}
                                                                workItemUuid={item.uuid}
                                                                isEditable={isWorkItemEditable}
                                                                dispatch={dispatch}
                                                                phase={activePhase}
                                                                onRecorded={onRecorded}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {canManageMeasurements && (
                                    <button
                                        onClick={() => requestUnlink(group.measurementUuid)}
                                        disabled={operationLoading || unlinkingUuid === group.measurementUuid}
                                        className="absolute top-0 right-0 p-1 text-gray-300 hover:text-red-500 hover:bg-white rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                                        title="Remove measurement"
                                    >
                                        {unlinkingUuid === group.measurementUuid
                                            ? <RefreshCw size={11} className="animate-spin" />
                                            : <X size={11} />}
                                    </button>
                                )}
                            </div>
                        );
                    }

                    if (isMultiPosition) {
                        return (
                            <div key={group.measurementUuid} className="relative group">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-xs font-semibold text-gray-700">{group.name}</span>
                                    {group.unitSymbol && <span className="text-xs text-gray-400 font-mono">{group.unitSymbol}</span>}
                                    <span className="text-[10px] text-gray-400">{group.items.length} positions</span>
                                </div>
                                <div className={`grid gap-1.5 grid-cols-1 ${
                                    group.items.length === 2 ? 'sm:grid-cols-2' :
                                        group.items.length === 3 ? 'sm:grid-cols-3' :
                                            'sm:grid-cols-2 md:grid-cols-4'
                                }`}>
                                    {group.items.map(obs => (
                                        <ObservationRow
                                            key={`${obs.measurementUuid}-${obs.positionLabel}`}
                                            obs={obs}
                                            jobUuid={job.uuid}
                                            workItemUuid={item.uuid}
                                            isEditable={isWorkItemEditable}
                                            dispatch={dispatch}
                                            compact={true}
                                            phase={activePhase}
                                            onRecorded={onRecorded}
                                        />
                                    ))}
                                </div>
                                {canManageMeasurements && (
                                    <button
                                        onClick={() => requestUnlink(group.measurementUuid)}
                                        disabled={operationLoading || unlinkingUuid === group.measurementUuid}
                                        className="absolute top-0 right-0 p-1 text-gray-300 hover:text-red-500 hover:bg-white rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                                        title="Remove measurement"
                                    >
                                        {unlinkingUuid === group.measurementUuid
                                            ? <RefreshCw size={11} className="animate-spin" />
                                            : <X size={11} />}
                                    </button>
                                )}
                            </div>
                        );
                    }

                    const obs = group.items[0];
                    return (
                        <div key={`${obs.measurementUuid}-${obs.positionLabel || 'single'}`} className="relative group">
                            <ObservationRow
                                obs={obs}
                                jobUuid={job.uuid}
                                workItemUuid={item.uuid}
                                isEditable={isWorkItemEditable}
                                dispatch={dispatch}
                                phase={activePhase}
                                onRecorded={onRecorded}
                            />
                            {canManageMeasurements && (
                                <button
                                    onClick={() => requestUnlink(obs.measurementUuid)}
                                    disabled={operationLoading || unlinkingUuid === obs.measurementUuid}
                                    className="absolute top-2 right-2 p-1 text-gray-300 hover:text-red-500 hover:bg-white rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                                    title="Remove measurement"
                                >
                                    {unlinkingUuid === obs.measurementUuid
                                        ? <RefreshCw size={11} className="animate-spin" />
                                        : <X size={11} />}
                                </button>
                            )}
                        </div>
                    );
                });
            })() : (
                <p className="text-xs text-gray-400 italic px-1">
                    No measurements yet — click <span className="font-semibold text-violet-600">Add</span> to record observations during the job.
                </p>
            )}

            {showMeasurementPicker && (
                <AddMeasurementPicker
                    jobUuid={job.uuid}
                    workItem={item}
                    onClose={() => setShowMeasurementPicker(false)}
                />
            )}

            {/* In-app confirmation for deleting (unlinking) a measurement.
                Replaces window.confirm() so the prompt matches the rest of
                the app and renders cleanly on tablet. */}
            <ConfirmDialog
                open={pendingUnlinkUuid != null}
                title="Delete measurement?"
                message="This will remove the measurement and any observations recorded against it for this work item. This action cannot be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="danger"
                onConfirm={handleConfirmUnlink}
                onCancel={() => setPendingUnlinkUuid(null)}
            />
        </div>
    );
};

// ── MeasurementRecorderModal ──────────────────────────────────────────────────
//
// Full-attention popup that hosts WorkItemMeasurementsPanel for ONE phase
// (IN or OUT). Launched from the big IN / OUT buttons on a WorkItemCard. This
// is the hand-held-friendly recording surface: tap the phase you mean, record,
// dismiss.
//
// Auto-dismiss rule (point 5):
//   • "Single-pick" item — exactly one linked measurement, single-slot
//     (no positions, no sub-positions). Recording one value/band is the whole
//     job, so the popup closes automatically on the first successful save.
//   • Everything else (multi-position, sub-position grids, or several
//     measurements) — the tech records as many readings as needed and closes
//     with the "Done" button.

// Counts distinct linked measurements for the given phase and whether the only
// one is a plain single-slot measurement (no position / sub-position rows).
const isSinglePickItem = (item, phase) => {
    const obs = (phase === 'OUT'
        ? item.measurementObservationsOut
        : item.measurementObservations) || [];
    if (obs.length === 0) return false;
    const distinct = new Set(obs.map(o => o.measurementUuid));
    if (distinct.size !== 1) return false;
    // Single-slot = no observation carries a position or sub-position label.
    return obs.every(o => !o.positionLabel && !o.subPositionLabel);
};

const MeasurementRecorderModal = ({ item, job, phase, isEditable, isWorkItemEditable, operationLoading, dispatch, onClose }) => {
    useEscapeToClose(true, onClose);

    // Decide auto-close once, up front, from the item's current shape. A
    // single-pick item closes on first save; anything else waits for "Done".
    const autoClose = isSinglePickItem(item, phase);

    const handleRecorded = () => {
        if (autoClose) {
            // Let the optimistic UI flash the saved state, then dismiss.
            setTimeout(() => onClose(), 250);
        }
    };

    const phaseLabel = phase === 'OUT' ? 'OUT' : 'IN';
    const phaseHint  = phase === 'OUT' ? 'Post-work readings' : 'Pre-work (baseline) readings';

    return (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
            <div
                /* max-h in dvh, not vh: on a phone `vh` counts the strip behind the
                   browser's own address bar, so the sheet is taller than what can be
                   seen and its last rows — including Done — sit under the chrome. dvh
                   is the visible viewport. */
                className="bg-white w-full sm:max-w-3xl lg:max-w-4xl sm:rounded-2xl rounded-t-2xl shadow-xl max-h-[92dvh] sm:max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
                    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl shrink-0 ${
                        phase === 'OUT' ? 'bg-sky-100 text-sky-600' : 'bg-violet-100 text-violet-600'
                    }`}>
                        <Activity size={18} />
                    </span>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-base font-bold text-gray-900 leading-tight">
                            Record {phaseLabel} measurements
                        </h2>
                        <p className="text-xs text-gray-500 truncate">{item.description} · {phaseHint}</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors shrink-0">
                        <X size={18} />
                    </button>
                </div>

                {/* Body — the existing recorder, phase-locked. Allowed to grow
                    into the wider modal; wide multi-position grids (e.g. 4 tyre
                    corners) scroll horizontally rather than squashing cells. */}
                {/* overscroll-contain + touch-pan-y: without them a drag that starts
                    inside this list hands the scroll to the page behind once the list
                    hits an end, which on a phone reads as "the popup will not scroll".
                    touch-pan-y tells the browser a vertical drag here is a scroll and
                    not a gesture it should wait to disambiguate. */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain [touch-action:pan-y] p-4 sm:p-5">
                    <WorkItemMeasurementsPanel
                        key={`${item.uuid}-${phase}`}
                        item={item}
                        job={job}
                        isEditable={isEditable}
                        isWorkItemEditable={isWorkItemEditable}
                        operationLoading={operationLoading}
                        dispatch={dispatch}
                        lockedPhase={phase}
                        onRecorded={handleRecorded}
                    />
                </div>

                {/* Footer — multi-slot items get a manual Done button. Single-pick
                    items auto-close, so the button is just a fallback exit. */}
                <div className="px-5 py-3 border-t border-gray-100 shrink-0 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 transition-colors"
                    >
                        {autoClose ? 'Close' : 'Done'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── ResourcePanel ─────────────────────────────────────────────────────────────

const ResourcePanel = ({ jobResources, availableResources, resourcesLoading, operationLoading, resourceTypeFilter, onFilterChange, onAssign, onUnassign }) => {
    const icon = (type, sm) => ({ EQUIPMENT: <Wrench size={sm} className="text-violet-500" />, TOOL: <Wrench size={sm} className="text-blue-500" />, SPACE: <MapPin size={sm} className="text-emerald-500" />, CONSULTANT: <Users size={sm} className="text-amber-500" /> }[type] || <Package size={sm} className="text-gray-400" />);
    const badge = (type) => ({ EQUIPMENT: 'bg-violet-100 text-violet-700', TOOL: 'bg-blue-100 text-blue-700', SPACE: 'bg-emerald-100 text-emerald-700', CONSULTANT: 'bg-amber-100 text-amber-700' }[type] || 'bg-gray-100 text-gray-600');

    return (
        <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Resources {jobResources.length > 0 && `(${jobResources.length} assigned)`}</p>
            {jobResources.length > 0 && (
                <div className="space-y-2 mb-4">
                    {jobResources.map(r => (
                        <div key={r.resourceUuid} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                {icon(r.resourceType, 13)}
                                <span className="text-sm font-medium text-gray-900 truncate">{r.name}</span>
                                {r.resourceIdentifier && <span className="text-xs text-gray-400 font-mono shrink-0">{r.resourceIdentifier}</span>}
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${badge(r.resourceType)}`}>{r.resourceType?.replace('_', ' ')}</span>
                            </div>
                            <button onClick={() => onUnassign(r)} disabled={operationLoading} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0" title="Release"><X size={14} /></button>
                        </div>
                    ))}
                </div>
            )}
            <div className="border rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b flex-wrap">
                    <Boxes size={14} className="text-gray-400 shrink-0" />
                    <span className="text-xs font-medium text-gray-500 mr-1">Available:</span>
                    {['', 'SPACE', 'CONSULTANT'].map(t => (
                        <button key={t} onClick={() => onFilterChange(t)} className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${resourceTypeFilter === t ? 'bg-violet-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-100'}`}>
                            {t === '' ? 'All' : t.charAt(0) + t.slice(1).toLowerCase().replace('_', ' ')}
                        </button>
                    ))}
                    {resourcesLoading && <RefreshCw size={12} className="animate-spin text-gray-400 ml-auto" />}
                </div>
                <div className="max-h-48 overflow-y-auto">
                    {resourcesLoading && availableResources.length === 0
                        ? <div className="py-6 text-center text-sm text-gray-400"><RefreshCw size={16} className="animate-spin mx-auto mb-1" /></div>
                        : availableResources.length === 0
                            ? <div className="py-5 text-center text-sm text-gray-400">No available resources{resourceTypeFilter ? ` of type ${resourceTypeFilter.toLowerCase()}` : ''}</div>
                            : <div className="divide-y">
                                {availableResources.map(r => (
                                    <button key={r.uuid} onClick={() => onAssign(r)} disabled={operationLoading} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-violet-50 transition-colors disabled:opacity-50">
                                        {icon(r.resourceType, 13)}
                                        <span className="text-sm font-medium text-gray-800 flex-1 truncate">{r.name}</span>
                                        {r.resourceIdentifier && <span className="text-xs text-gray-400 font-mono shrink-0">{r.resourceIdentifier}</span>}
                                        {r.baseCostPerDay != null && <span className="text-xs text-gray-500 shrink-0">${Number(r.baseCostPerDay).toFixed(2)}/day</span>}
                                        <Plus size={14} className="text-violet-500 shrink-0" />
                                    </button>
                                ))}
                            </div>
                    }
                </div>
            </div>
        </div>
    );
};

// ── JobApprovalCard ───────────────────────────────────────────────────────────

/**
 * Job-level customer approval card for PROFILE approval mode.
 * Shows a text area for the authorisation message and a button to send it.
 *
 * Calls PUT /jobs/{jobUuid}/work-items/{jobUuid} with description + confirmationRequired
 * — the duplicated UUID signals job-level scope to the backend.
 *
 * Reused in both JobDetailModal and WorkItemPanel (tablet).
 */
const JobApprovalCard = ({ job, isEditable, operationLoading, dispatch }) => {
    const [message, setMessage] = useState(job.lastApprovalMessage || '');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    // Pre-flight warning: lists work items with un-recorded IN measurements
    // when the mechanic tries to send. Null = dialog closed; array = open.
    const [missingInItems, setMissingInItems] = useState(null);
    const busy = operationLoading || sending;

    // Derived from the confirmation_request row status — SENT means the customer
    // hasn't responded yet. Decoupled from job status so manual pause/resume
    // doesn't break the disabled state.
    const awaitingApproval = job.lastApprovalStatus === 'SENT';

    // Keep the textarea in sync when the job detail refreshes (e.g. after a
    // re-fetch) and the previous message comes back from the server.
    useEffect(() => {
        if (job.lastApprovalMessage && !message) {
            setMessage(job.lastApprovalMessage);
        }
    }, [job.lastApprovalMessage]); // eslint-disable-line react-hooks/exhaustive-deps

    // Actually fire the request. Split out from handleRequestApproval so the
    // IN-values warning dialog can call it after the mechanic confirms.
    const sendApprovalRequest = async () => {
        setError(null);
        setSending(true);
        try {
            await dispatch(requestJobApproval({ jobUuid: job.uuid, description: message })).unwrap();
        } catch {
            // error surfaced via slice toast
        } finally {
            setSending(false);
        }
    };

    const handleRequestApproval = async () => {
        if (!job.uuid || busy || awaitingApproval) return;
        if (!message.trim()) {
            setError('Please enter an authorisation message');
            return;
        }
        // Job-level approval covers every work item on the job, so we check IN
        // measurements across the full work-item list. If any are missing the
        // mechanic gets a warning dialog — they can proceed (intentional
        // pre-inspection quote) or cancel and go fill them in.
        const missing = findItemsMissingInValues(job.workItems || []);
        if (missing.length > 0) {
            setMissingInItems(missing);
            return;
        }
        await sendApprovalRequest();
    };

    // When a request is in flight, hide this entire card. The audit row in
    // ApprovalHistoryList directly below carries the same signal (status pill
    // "Awaiting Response", item names, who sent it, when) so the amber banner
    // here was duplicating information.
    if (awaitingApproval) return null;

    return (
        <>
            <div className="border rounded-xl p-4 space-y-3 bg-violet-50 border-violet-200">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-violet-100">
                        <Send size={16} className="text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">
                            Job-level Customer Approval
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Send a single authorisation request covering all work items
                        </p>
                    </div>
                </div>
                {isEditable && (
                    <div className="space-y-2">
                        <textarea
                            value={message}
                            onChange={(e) => { setMessage(e.target.value); if (error) setError(null); }}
                            placeholder="Enter authorisation message for the customer…"
                            rows={2}
                            disabled={busy}
                            className={`w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50 ${error ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                        />
                        {error && <p className="text-xs text-red-500">{error}</p>}
                        <div className="flex justify-end">
                            <button
                                onClick={handleRequestApproval}
                                disabled={busy || !message.trim()}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-semibold hover:bg-violet-700 transition-colors disabled:opacity-50"
                            >
                                {sending ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                                {sending ? 'Sending…' : 'Request Authorisation'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* IN-values pre-flight warning. The customer is about to be asked
                to authorise work without inspection data — surface that explicitly
                so the mechanic can decide whether to proceed or go fill in the
                missing IN values first. Scope is the entire job's work item list. */}
            <ConfirmDialog
                open={missingInItems != null}
                title="Some IN values aren't recorded"
                variant="warning"
                confirmLabel="Send anyway"
                cancelLabel="Go back"
                message={
                    missingInItems
                        ? `${missingInItems.length} work item${missingInItems.length === 1 ? '' : 's'} on this job ` +
                        `${missingInItems.length === 1 ? 'has' : 'have'} IN measurements that haven't been recorded yet: ` +
                        `${missingInItems.map(i => i.description || 'Untitled').slice(0, 5).join(', ')}` +
                        `${missingInItems.length > 5 ? ` (+ ${missingInItems.length - 5} more)` : ''}. ` +
                        `The customer will be asked to authorise work without complete inspection data. Send anyway?`
                        : ''
                }
                onConfirm={async () => {
                    setMissingInItems(null);
                    await sendApprovalRequest();
                }}
                onCancel={() => setMissingInItems(null)}
            />
        </>
    );
};

// ── ApprovalHistoryList ───────────────────────────────────────────────────────

/**
 * Constant used as the request body when sending a batch approval without
 * a user-typed message. Lives at module scope so both the history list (to
 * hide the row's message when it matches the default) and BatchApprovalCard
 * (to send it) can reference the same string.
 */
const DEFAULT_BATCH_APPROVAL_MESSAGE = 'Please approve following item(s)';

/**
 * Audit-trail list of every customer approval request that has been sent for
 * this job. Rendered underneath the interactive approval card (JobApprovalCard
 * or BatchApprovalCard) on the Customer Approvals tab.
 *
 * <p>This is intentionally a read-only surface — once a request has been sent,
 * the row stays visible regardless of subsequent customer responses. Each entry
 * shows its outcome (SENT / APPROVED / DECLINED / EXPIRED) with a colour-coded
 * status pill, the message the customer saw, who sent it, when, and (when
 * applicable) when the customer responded.
 *
 * <p>The per-work-item CONFIRMED state on the work item card is unaffected —
 * that's a separate per-item visual. This panel exists so the workshop can
 * still see the full request history even after items have been resolved and
 * the actionable cards above collapse to the empty state.
 */
// Backend ConfirmationStatus values:
//   SENT       — link issued, customer hasn't opened/responded yet
//   RESPONDED  — customer has submitted their itemised decisions (mix of approve/decline)
//   APPROVED   — single-item legacy path, fully approved
//   DECLINED   — single-item legacy path, fully declined
//   EXPIRED    — link expiry passed before any response
//
// RESPONDED is the common terminal state for the job-level / batch flow because a
// customer can approve some items and decline others — the rolled-up per-item
// outcome lives on the work items themselves (customerConfirmed) and on the job's
// customerBasedWorkItemStatus field, not on this single status enum. So we render
// RESPONDED as a neutral "Responded" pill here; the per-work-item outcome surfaces
// on the WorkItemCard above, not inside this audit row.
const APPROVAL_STATUS_META = {
    SENT:      { label: 'Awaiting Response', pillClass: 'bg-amber-100 text-amber-700',     iconClass: 'text-amber-600',   Icon: Clock         },
    RESPONDED: { label: 'Responded',         pillClass: 'bg-sky-100 text-sky-700',         iconClass: 'text-sky-600',     Icon: CheckCircle   },
    APPROVED:  { label: 'Approved',          pillClass: 'bg-emerald-100 text-emerald-700', iconClass: 'text-emerald-600', Icon: CheckCircle   },
    DECLINED:  { label: 'Declined',          pillClass: 'bg-red-100 text-red-700',         iconClass: 'text-red-600',     Icon: XCircle       },
    EXPIRED:   { label: 'Expired',           pillClass: 'bg-gray-200 text-gray-600',       iconClass: 'text-gray-500',    Icon: AlertTriangle },
};

const formatApprovalTimestamp = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    // Compact local format — date + 24h time, no seconds. Matches the convention
    // used elsewhere in the modal (booking timestamps, note metadata).
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
};

/**
 * What the customer actually decided, item by item.
 *
 * The Approval History below lists REQUESTS — sent when, answered when. For a
 * job-level request, which is what every quote and every batch approval uses, one row
 * covers the whole job and says nothing about which items were approved and which were
 * refused. That is precisely the question asked months later when an invoice is
 * disputed, so it gets its own list.
 *
 * Only priced items appear. A zero-cost inspection line was never sent for approval —
 * there is nothing to authorise at $0 — and listing it as "not decided" would invent an
 * open question that never existed.
 */
const CustomerDecisionList = ({ workItems }) => {
    const priced = (workItems || []).filter(wi => workItemTotal(wi) > 0);

    // 🔑 COLLAPSED by default. A quote raised from an imported one carries a line per
    // priced item — twelve on a log-book service — and expanded it pushes Send for
    // approval off the bottom of the modal, so the action the tab exists for is the one
    // thing you cannot see. The header carries the summary that answers most visits;
    // opening it is for when a specific item is in question.
    const [open, setOpen] = useState(false);

    if (priced.length === 0) return null;

    const undecided = priced.filter(wi => wi.customerConfirmed !== true
        && wi.customerConfirmed !== false).length;

    return (
        <div className="border rounded-xl bg-white border-gray-200">
            <button type="button" onClick={() => setOpen(o => !o)}
                    aria-expanded={open}
                    className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 rounded-xl">
                <UserCheck size={14} className="text-gray-500" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Customer decisions
                </p>
                <span className="ml-auto text-xs text-gray-400">
                    {/* The state, not just the count — that is what a glance is for. */}
                    {undecided > 0
                        ? `${undecided} of ${priced.length} awaiting the customer`
                        : `${priced.length} priced ${priced.length === 1 ? 'item' : 'items'} — all answered`}
                </span>
                {open ? <ChevronUp size={14} className="text-gray-400" />
                      : <ChevronDown size={14} className="text-gray-400" />}
            </button>
            {open && (
            <ul className="divide-y divide-gray-100 border-t border-gray-100">
                {priced.map(wi => {
                    const decided = wi.customerConfirmed === true || wi.customerConfirmed === false;
                    const approved = wi.customerConfirmed === true;
                    const when = formatApprovalTimestamp(wi.customerConfirmedAt);

                    return (
                        <li key={wi.uuid} className="px-4 py-3 flex items-start gap-3">
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-800 break-words">
                                    {wi.description}
                                </p>
                                <p className={`text-xs mt-0.5 ${
                                    !decided ? 'text-gray-500'
                                        : approved ? 'text-emerald-700' : 'text-red-700'}`}>
                                    {!decided
                                        ? 'Awaiting the customer'
                                        : `${approved ? 'Approved' : 'Declined'} by the customer${
                                            when ? ` on ${when}` : ''}`}
                                </p>
                                {/* A decision with no time is an item answered before the
                                    timestamp was recorded. Saying so beats leaving a reader
                                    to wonder whether the date is missing or the approval is. */}
                                {decided && !when && (
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                        Time not recorded for this decision
                                    </p>
                                )}
                            </div>
                            <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                                !decided ? 'bg-gray-100 text-gray-600'
                                    : approved ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-red-100 text-red-700'}`}>
                                {!decided ? 'Pending' : approved ? 'Approved' : 'Declined'}
                            </span>
                        </li>
                    );
                })}
            </ul>
            )}
        </div>
    );
};

const ApprovalHistoryList = ({ history }) => {
    if (!Array.isArray(history) || history.length === 0) return null;

    return (
        <div className="border rounded-xl bg-white border-gray-200">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
                <History size={14} className="text-gray-500" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Approval History</p>
                <span className="ml-auto text-xs text-gray-400">{history.length} {history.length === 1 ? 'request' : 'requests'}</span>
            </div>
            <ul className="divide-y divide-gray-100">
                {history.map(entry => {
                    const meta = APPROVAL_STATUS_META[entry.status] || APPROVAL_STATUS_META.SENT;
                    const Icon = meta.Icon;
                    const scopeLabel = entry.scope === 'JOB'
                        ? 'Job-level approval'
                        : (entry.workItemDescription || 'Work item');
                    const sentLine = (() => {
                        const ts = formatApprovalTimestamp(entry.sentAt);
                        if (!ts) return null;
                        return entry.sentByName
                            ? `Sent ${ts} by ${entry.sentByName}`
                            : `Sent ${ts}`;
                    })();
                    const respondedLine = entry.respondedAt
                        ? `${meta.label} ${formatApprovalTimestamp(entry.respondedAt)}`
                        : null;

                    return (
                        <li key={entry.uuid} className="px-4 py-3 space-y-1.5">
                            <div className="flex items-start gap-2">
                                <Icon size={14} className={`mt-0.5 shrink-0 ${meta.iconClass}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-gray-900 truncate" title={scopeLabel}>{scopeLabel}</span>
                                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.pillClass}`}>
                                            {meta.label}
                                        </span>
                                    </div>
                                    {/*
                                     * Message body — hidden when it matches the boilerplate batch-approval
                                     * default. The default carries no information beyond what the row's
                                     * scope label and (where present) work item list already convey, so
                                     * suppressing it removes visual noise. Custom user-typed messages
                                     * (e.g. PROFILE-mode "Please confirm the extra brake work") continue
                                     * to render in full.
                                     */}
                                    {entry.message && entry.message.trim() !== DEFAULT_BATCH_APPROVAL_MESSAGE && (
                                        <p className="text-xs text-gray-600 mt-1 leading-relaxed whitespace-pre-wrap break-words">
                                            {entry.message}
                                        </p>
                                    )}
                                    {/*
                                     * How many items the request covered — the NAMES are
                                     * deliberately not repeated here.
                                     *
                                     * They used to be, as a bullet per item, and on a
                                     * log-book service that is a twelve-line block saying
                                     * exactly what Customer decisions says directly above,
                                     * minus the one thing that matters: whether each was
                                     * approved. Two lists of the same twelve items, the
                                     * shorter one strictly less useful, pushed the Sent and
                                     * Responded lines — the only facts this row owns — off
                                     * the bottom of the card.
                                     *
                                     * A work-item request names its item in the title, so
                                     * the count would be "1 item" beside it; JOB scope is
                                     * the only place a count says anything.
                                     *
                                     * Shapes tolerated (any of):
                                     *   plain string                              ← workItemNames
                                     *   { description, uuid }
                                     *   { workItemDescription, workItemUuid }
                                     */}
                                    {(() => {
                                        if (entry.scope !== 'JOB') return null;
                                        const items =
                                            (Array.isArray(entry.workItemNames) && entry.workItemNames)
                                            || (Array.isArray(entry.items) && entry.items)
                                            || (Array.isArray(entry.workItems) && entry.workItems)
                                            || (Array.isArray(entry.requestedItems) && entry.requestedItems)
                                            || null;
                                        if (!items || items.length === 0) return null;
                                        return (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Covered {items.length} work {items.length === 1 ? 'item' : 'items'} — see Customer decisions above.
                                            </p>
                                        );
                                    })()}
                                    <div className="text-[11px] text-gray-400 mt-1.5 space-y-0.5">
                                        {sentLine && <p>{sentLine}</p>}
                                        {respondedLine && <p>{respondedLine}</p>}
                                    </div>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

// ── BatchApprovalCard ─────────────────────────────────────────────────────────

/**
 * Batch customer approval card for WORK_ITEM approval mode.
 * Lets the mechanic select multiple ad-hoc work items and request authorisation
 * for them in a single request — covering scenarios like "engine issue + tyres
 * + shocks discovered during inspection, all needing customer approval at once."
 *
 * Reuses the existing job-level approval endpoint (POST /jobs/{uuid}/request-approval)
 * via the requestJobApproval thunk — same backend path as PROFILE-mode approval.
 * When the customer approves, all items linked to the request flip to confirmed
 * together (mirroring PROFILE-mode batch approval behaviour).
 *
 * Only rendered for non-PROFILE jobs. The per-item confirmationRequired flow
 * remains untouched and continues to work alongside this batch path — the
 * mechanic can choose to send individual requests via the work item form OR
 * batch multiple items here, depending on the situation.
 *
 * Eligibility: items that are undecided AND have a dollar value (labour or
 * parts > 0). Zero-cost inspection/check items are never sent for customer
 * approval — see isApprovable at the top of the file.
 */

const BatchApprovalCard = ({ job, isEditable, operationLoading, dispatch }) => {
    const [selectedUuids, setSelectedUuids] = useState(new Set());
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    // Pre-flight warning: lists selected work items with un-recorded IN
    // measurements. Null = dialog closed; array = open.
    const [missingInItems, setMissingInItems] = useState(null);
    const busy = operationLoading || sending;

    // The authorisation message sent to the customer is set internally — there's
    // no user-editable field for it. Techs select work items and submit; the
    // server receives the constant DEFAULT_BATCH_APPROVAL_MESSAGE as the
    // approval description.

    // Mirrors JobApprovalCard — SENT means a previous batch is awaiting customer response
    const awaitingApproval = job.lastApprovalStatus === 'SENT';

    // Eligible items: undecided AND has a dollar value. Zero-cost inspection
    // lines (e.g. "Horn - Check/report") are workshop tasks, never sent for
    // customer approval — see isApprovable / workItemTotal at top of file.
    const eligibleItems = (job.workItems || []).filter(isApprovable);

    const toggle = (uuid) => {
        setSelectedUuids(prev => {
            const next = new Set(prev);
            if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
            return next;
        });
    };

    const selectedCount = selectedUuids.size;

    // Actually fire the request. Split out so the IN-values warning dialog
    // can call it after the mechanic confirms.
    const sendApprovalRequest = async () => {
        setError(null);
        setSending(true);
        try {
            await dispatch(requestJobApproval({
                jobUuid: job.uuid,
                description: DEFAULT_BATCH_APPROVAL_MESSAGE,
            })).unwrap();
            setSelectedUuids(new Set());
        } catch {
            // error surfaced via slice toast
        } finally {
            setSending(false);
        }
    };

    const handleRequestApproval = async () => {
        if (!job.uuid || busy || awaitingApproval) return;
        if (selectedCount === 0) {
            setError('Select at least one work item to bundle');
            return;
        }
        // Check ALL work items on the job for missing IN measurements — not
        // just the selected batch. The mechanic should know about un-recorded
        // inspection data anywhere on the job before sending anything to the
        // customer, since the approval email is one step in a wider workflow
        // and missing IN values elsewhere often signal the job isn't ready.
        const missing = findItemsMissingInValues(job.workItems || []);
        if (missing.length > 0) {
            setMissingInItems(missing);
            return;
        }
        await sendApprovalRequest();
    };

    // Hide the card entirely when there's nothing to bundle, OR when a previous
    // request is in flight. The audit row in ApprovalHistoryList directly below
    // carries the in-flight signal (status pill "Awaiting Response", item names,
    // who sent it, when) so the amber banner was duplicating that information.
    if (awaitingApproval) return null;
    if (eligibleItems.length === 0) return null;

    return (
        <>
            <div className="border rounded-xl p-4 space-y-3 bg-violet-50 border-violet-200">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-violet-100">
                        <Send size={16} className="text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">
                            Customer Approvals
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Work items to request approvals
                        </p>
                    </div>
                    {/* Select all / Clear toggle — top-right corner of the card.
                        Pure client-side convenience: produces the exact same
                        selectedUuids state as ticking every checkbox by hand,
                        so downstream submit logic is unchanged. */}
                    {isEditable && eligibleItems.length > 0 && (
                        <button
                            type="button"
                            onClick={() => {
                                if (selectedCount === eligibleItems.length) {
                                    setSelectedUuids(new Set());
                                } else {
                                    setSelectedUuids(new Set(eligibleItems.map(i => i.uuid)));
                                }
                            }}
                            disabled={busy}
                            className="shrink-0 text-xs font-semibold text-violet-700 hover:text-violet-800 disabled:opacity-50"
                        >
                            {selectedCount === eligibleItems.length ? 'Clear' : 'Select all'}
                        </button>
                    )}
                </div>

                {isEditable && eligibleItems.length > 0 && (
                    <>
                        {/* Item picker */}
                        <div className="bg-white border border-violet-100 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                            {eligibleItems.map(item => {
                                const total = workItemTotal(item);
                                const checked = selectedUuids.has(item.uuid);
                                return (
                                    <label
                                        key={item.uuid}
                                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-violet-50/50 transition-colors ${checked ? 'bg-violet-50/70' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(item.uuid)}
                                            disabled={busy}
                                            className="w-4 h-4 accent-violet-600 shrink-0"
                                        />
                                        <span className="flex-1 text-sm text-gray-800 truncate" title={item.description}>
                                            {item.description}
                                        </span>
                                        {item.fromServiceProfile && (
                                            <span className="shrink-0 px-1.5 py-0.5 bg-violet-100 text-violet-600 rounded text-xs font-medium">Profile</span>
                                        )}
                                        {total > 0 && (
                                            <span className="shrink-0 text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">
                                                ${total.toFixed(2)}
                                            </span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>

                        {/* Selection summary — show only the count.
                            The per-item $ amounts on each row are kept so the tech can
                            still see what they're bundling, but the total roll-up line
                            is intentionally hidden in this view (it's a customer-facing
                            approval request, not an invoice — pricing is finalised when
                            the invoice is generated). */}
                        {selectedCount > 0 && (
                            <div className="flex items-center justify-between text-xs text-gray-600 px-1">
                                <span>{selectedCount} of {eligibleItems.length} selected</span>
                            </div>
                        )}

                        {/* Submit — the authorisation message is set internally (see
                            DEFAULT_BATCH_APPROVAL_MESSAGE) and is not user-editable here.
                            Removing the textarea keeps the surface focused on what the
                            tech actually needs to do: pick items and request. */}
                        <div className="space-y-2">
                            {error && <p className="text-xs text-red-500">{error}</p>}
                            <div className="flex justify-end">
                                <button
                                    onClick={handleRequestApproval}
                                    disabled={busy || selectedCount === 0}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-semibold hover:bg-violet-700 transition-colors disabled:opacity-50"
                                >
                                    {sending ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                                    {sending
                                        ? 'Sending…'
                                        : selectedCount === 0
                                            ? 'Request Authorisation'
                                            : `Request Authorisation for ${selectedCount} item${selectedCount === 1 ? '' : 's'}`}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* IN-values pre-flight warning. Scope is the entire job's work
                item list — un-recorded inspection data anywhere on the job
                should be surfaced before any approval email goes out. */}
            <ConfirmDialog
                open={missingInItems != null}
                title="Some IN values aren't recorded"
                variant="warning"
                confirmLabel="Send anyway"
                cancelLabel="Go back"
                message={
                    missingInItems
                        ? `${missingInItems.length} work item${missingInItems.length === 1 ? '' : 's'} on this job ` +
                        `${missingInItems.length === 1 ? 'has' : 'have'} IN measurements that haven't been recorded yet: ` +
                        `${missingInItems.map(i => i.description || 'Untitled').slice(0, 5).join(', ')}` +
                        `${missingInItems.length > 5 ? ` (+ ${missingInItems.length - 5} more)` : ''}. ` +
                        `The customer will be asked to authorise work without complete inspection data. Send anyway?`
                        : ''
                }
                onConfirm={async () => {
                    setMissingInItems(null);
                    await sendApprovalRequest();
                }}
                onCancel={() => setMissingInItems(null)}
            />
        </>
    );
};

// ── OdometerInTile ────────────────────────────────────────────────────────────

/**
 * Inline-editable header tile for the vehicle-in odometer reading.
 *
 * Click to edit, Enter or blur to save, Escape to cancel. Always rendered (no
 * conditional like the legacy read-only tile) so the technician can capture
 * the reading at any point in the job lifecycle — including after work has
 * already started or been paused. Persists via PATCH /jobs/{uuid}/odometer-in
 * which is scoped to this single field and works across every job status.
 *
 * Visual shell mirrors the surrounding bg-gray-50 stat tiles so it sits in the
 * grid without standing out until interacted with.
 */
const OdometerInTile = ({ job, dispatch }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(job.odometerIn != null ? String(job.odometerIn) : '');
    const [saving, setSaving] = useState(false);

    // Keep local input synced when the job prop refreshes — e.g. another tab
    // saved a new reading, or the parent re-fetched after an unrelated update.
    useEffect(() => {
        if (!editing) setValue(job.odometerIn != null ? String(job.odometerIn) : '');
    }, [job.odometerIn, editing]);

    const commit = async () => {
        const trimmed = value.trim();
        // Empty input while editing → treat as cancel rather than send null.
        // The endpoint rejects null, and clearing an odometer reading isn't a
        // workflow we want to expose via this lightweight inline edit.
        if (trimmed === '') {
            setEditing(false);
            setValue(job.odometerIn != null ? String(job.odometerIn) : '');
            return;
        }
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed) || parsed < 0) {
            // Reject silently — bad input just snaps back. Validation message
            // would be overkill for a single numeric field.
            setEditing(false);
            setValue(job.odometerIn != null ? String(job.odometerIn) : '');
            return;
        }
        // No-op when unchanged.
        if (parsed === job.odometerIn) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await dispatch(recordOdometerIn({ uuid: job.uuid, odometerIn: parsed })).unwrap();
            setEditing(false);
        } catch {
            // Error surfaced via slice toast; revert local input.
            setValue(job.odometerIn != null ? String(job.odometerIn) : '');
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-gray-50 rounded-lg px-2.5 py-1.5">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Odometer In</p>
            {editing ? (
                <input
                    type="number"
                    min="0"
                    inputMode="numeric"
                    autoFocus
                    value={value}
                    disabled={saving}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commit(); }
                        else if (e.key === 'Escape') {
                            e.preventDefault();
                            setValue(job.odometerIn != null ? String(job.odometerIn) : '');
                            setEditing(false);
                        }
                    }}
                    placeholder="e.g. 145000"
                    className="w-full text-sm font-semibold text-gray-900 bg-white border border-violet-300 rounded px-1 py-0 leading-tight focus:outline-none focus:ring-1 focus:ring-violet-400"
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="text-sm font-semibold text-gray-900 leading-tight hover:text-violet-700 text-left w-full"
                    title="Click to record odometer reading"
                >
                    {job.odometerIn != null
                        ? `${job.odometerIn.toLocaleString()} km`
                        : <span className="text-gray-400 italic font-normal">Tap to record</span>}
                </button>
            )}
        </div>
    );
};

// ── JobDetailModal ────────────────────────────────────────────────────────────

/**
 * The customer on a job card, with their email and mobile fixable in place.
 *
 * <h2>🔑 Why the contact details are here and not only on the customer record</h2>
 *
 * Approval is an EMAIL. Without one the backend refuses — "customer has no email address
 * on file" — and until now the card showed a name and a phone, so the only way to learn
 * that was to press Send for approval and have it fail. Migrated customers are routinely
 * missing one: the Customers export carries an email, the Jobs and Credit Notes exports
 * frequently do not.
 *
 * <p>The mobile is here for the same reason and a second one: it is the number rung when
 * the car is ready, it is wrong or absent on plenty of migrated rows, and the moment
 * anyone notices is while looking at the job — not later, on the Customers tab.
 *
 * <p>⚠️ It edits the CUSTOMER, the same record every other job and invoice of theirs
 * reads — so it is worth doing once, and the form says so rather than leaving that to be
 * discovered when it changes somewhere else.
 *
 * <p>🪤 A blank field means LEAVE IT ALONE, never "clear it" — `CustomerService.update`
 * skips a null email and skips a blank phone. So clearing a detail is deliberately not
 * possible here, and sending `''` would be a silent no-op rather than the erase it looks
 * like. Same rule, and the same `|| null`, as the invoice contact editor.
 */
function CustomerContactCard({ job }) {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ email: '', phone: '' });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    // What this card has saved since it mounted, so the display updates without a
    // refetch of the whole job. null = nothing saved here yet.
    const [saved, setSaved] = useState(null);

    const email = saved?.email ?? job.customerEmail;
    const phone = saved?.phone ?? job.customerPhone;
    // 🔴 Same rule as the send button: a field the server never sent is not an empty one.
    const emailKnown = saved !== null || Object.prototype.hasOwnProperty.call(job, 'customerEmail');

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const next = { email: form.email.trim() || null, phone: form.phone.trim() || null };
            await updateCustomerApi(job.customerUuid, next);
            setSaved({ email: next.email ?? email, phone: next.phone ?? phone });
            setEditing(false);
        } catch (err) {
            setError(err?.response?.data?.message
                || 'Could not save. That email may already belong to another customer.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-gray-50 rounded-lg px-2.5 py-1.5">
            <div className="flex items-start justify-between gap-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Customer</p>
                {!editing && job.customerUuid && (
                    <button type="button"
                        onClick={() => {
                            setForm({ email: email || '', phone: phone || '' });
                            setError(null);
                            setEditing(true);
                        }}
                        className="text-[10px] font-medium text-violet-600 hover:text-violet-700">
                        {email || phone ? 'Edit' : 'Add contact'}
                    </button>
                )}
            </div>
            <p className="text-sm font-semibold text-gray-900 truncate leading-tight">{job.customerName}</p>

            {!editing ? (
                <>
                    {phone
                        ? <p className="text-[11px] text-gray-500 leading-tight">{phone}</p>
                        : <p className="text-[11px] text-gray-400 leading-tight italic">No mobile</p>}
                    {email
                        ? <p className="text-[11px] text-gray-500 leading-tight truncate">{email}</p>
                        // 🔑 Says the consequence, not just the absence — and only when the
                        // server actually told us there is none.
                        : emailKnown && (
                            <p className="text-[11px] text-amber-700 leading-tight">
                                No email — cannot send for approval
                            </p>
                        )}
                </>
            ) : (
                <div className="mt-1 space-y-1">
                    <input type="email" value={form.email} autoFocus
                        onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                        aria-label="Email address" placeholder="Email address"
                        className="w-full px-2 py-1 border border-gray-200 rounded text-[11px] outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500" />
                    <input type="tel" value={form.phone}
                        onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                        aria-label="Mobile" placeholder="Mobile"
                        className="w-full px-2 py-1 border border-gray-200 rounded text-[11px] outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500" />
                    {error && <p className="text-[10px] text-red-700">{error}</p>}
                    <p className="text-[10px] text-gray-500">Saves on the customer.</p>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={save} disabled={busy}
                            className="text-[10px] font-semibold bg-violet-600 text-white px-2 py-0.5 rounded hover:bg-violet-700 disabled:opacity-50">
                            {busy ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" onClick={() => setEditing(false)}
                            className="text-[10px] text-gray-500 hover:text-gray-700">Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * What to say after the invoice number: whether it has been paid, else its status.
 *
 * 🔑 `invoicePaid` is the server's own answer, not a reading of the status string —
 * PARTIALLY_PAID and TRADE_CREDIT are neither plainly paid nor plainly unpaid, and
 * deciding that here would be a second, weaker copy of a rule the front end does not
 * own. Null when the server sent neither.
 */
export function invoiceDetailText(job) {
    if (job?.invoicePaid === true) return 'Paid';
    if (job?.invoiceStatus) return job.invoiceStatus.replace(/_/g, ' ').toLowerCase();
    return null;
}

const JobDetailModal = ({ job, operationLoading, detailLoading = false, onOpenInvoice = null, operationError, onClearError, jobResources, availableResources, resourcesLoading, workCategories, categoryTypes = [], initialTab = 'details', onClose, onBack, backLabel, onStart, onPause, onResume, onOpenComplete, onOpenVehicleCollect, onOpenAssign, onAddWorkItem, onEditWorkItem, onRemoveWorkItem, onAddNote, onRefreshJob, onAssignResource, onUnassignResource, resourceTypeFilter, onResourceTypeFilter, onSendQuote }) => {
    const dispatch = useDispatch();
    // Pressed, and on its way. The card is about to be replaced by the invoice, so the
    // control says so and stops accepting presses rather than sitting dead.
    const [openingInvoice, setOpeningInvoice] = useState(false);

    /**
     * To the invoice.
     *
     * 🔑 NO router hook here. This card is rendered by five pages and by tests that
     * mount it on its own; calling useNavigate would make a Router a requirement of
     * the card, and two suites that render it bare would fail for a reason that has
     * nothing to do with what they test. The caller passes the soft navigation it
     * already has (see useOpenInvoice); the fallback is an ordinary link to the same
     * deep URL, which works anywhere.
     */
    const openInvoice = useCallback((uuid) => {
        setOpeningInvoice(true);
        if (onOpenInvoice) { onOpenInvoice(uuid); return; }
        window.location.assign(invoiceDeepLink(uuid));
    }, [onOpenInvoice]);

    const organisationType = useSelector((s) => s.auth.user?.organisationType);
    const [activeTab,       setActiveTab]       = useState(initialTab);
    const [showAddModal,    setShowAddModal]    = useState(false);
    const [showEditModal,   setShowEditModal]   = useState(false);
    const [editingItem,     setEditingItem]     = useState(null);
    // Work-item removal goes through the in-app ConfirmDialog rather than
    // window.confirm so the prompt stays inside the modal stack and styled
    // consistently with the rest of the UI.
    const [pendingRemoveUuid, setPendingRemoveUuid] = useState(null);

    // Delete-job confirm dialog state. The job-number typed by the user
    // gates the destructive call so an accidental tap can never purge a
    // job and its booking + invoice in one go.
    const [showDeleteJobDialog, setShowDeleteJobDialog] = useState(false);
    const [deleteJobInput,       setDeleteJobInput]       = useState('');

    // Desktop-only Escape to close. The shared stack means any open child
    // (AddWorkItemModal, ConfirmDialog, delete-job dialog) will consume the
    // Escape first; this modal only closes once those have been dismissed.
    //
    // When opened with a back affordance (e.g. from BookingsPage's "View Job"),
    // Escape mirrors the back button — returns to the previous modal instead
    // of closing the whole stack. This keeps the keyboard shortcut consistent
    // with what staff see on screen.
    useEscapeToClose(true, onBack || onClose);

    // Editability has been relaxed: the job detail panel is now editable at every
    // stage — including QUEUED (CREATED / QUOTED / APPROVED) before any work has
    // started AND after the job has been completed/invoiced/collected. This
    // matches how shop floors actually operate: a tech often needs to fix a
    // typo on a completed work item, add a forgotten part to an invoiced job,
    // or tidy up notes on a vehicle that's already been collected.
    //
    // isEditable controls job-level edits (add work item, manage resources,
    // request customer approval).
    // isWorkItemEditable controls per-work-item edits (observation sliders,
    // edit/remove buttons, status changes via the checkbox toggle).
    // Both are now always true — backend authorisation rules remain the source
    // of truth for what's actually permitted.
    const isEditable = true;
    const isWorkItemEditable = true;

    // A quote the customer has not answered yet. Both halves are needed: `quote` is
    // provenance and is never cleared, so it stays true on the job the quote became —
    // the status is what says it is still awaiting an answer.
    const isPendingQuote = !!job.quote && job.status === 'QUOTED';

    /**
     * A job with no booking — an approved quote, or any job created standalone.
     *
     * <p>It has a customer and a vehicle but no appointment, so there is nothing to say
     * when the car is coming or that it has arrived. Both are offered here rather than
     * sending somebody to the bookings page to make a second, unrelated booking.
     */
    const needsBooking = !isPendingQuote && !job.bookingUuid
        && ['CREATED', 'QUOTED'].includes(job.status);
    /** Booked in, car not here yet. The mechanic holding the keys says so from here. */
    const awaitingVehicle = !!job.bookingUuid && job.bookingStatus === 'SCHEDULED';

    const [showBookingDialog, setShowBookingDialog] = useState(false);
    const [showHandoverModal, setShowHandoverModal] = useState(false);

    // The handover form's photo categories are server-driven and live in the bookings
    // slice. Fetched only for a job that could actually need them — most jobs have no
    // loan car, and a request per opened job would be waste.
    const loanCarImageCategories = useSelector((s) => s.bookings?.loanCarImageCategories ?? []);
    useEffect(() => {
        if (job.bookingHasLoanCar && loanCarImageCategories.length === 0) {
            dispatch(fetchLoanCarImageCategories());
        }
    }, [dispatch, job.bookingHasLoanCar, loanCarImageCategories.length]);
    const [bookingDate, setBookingDate] = useState('');
    const [bookingTime, setBookingTime] = useState('');
    const [bookingError, setBookingError] = useState(null);

    const handleBookIn = async () => {
        setBookingError(null);
        const result = await dispatch(scheduleJob({
            jobUuid: job.uuid, scheduledDate: bookingDate, scheduledTime: bookingTime || null,
        }));
        if (scheduleJob.rejected.match(result)) {
            // The server names what is wrong — already booked, and for when.
            setBookingError(result.payload);
            return;
        }
        setShowBookingDialog(false);
        setBookingDate('');
        setBookingTime('');
        onRefreshJob?.();
    };

    /**
     * ⚠️ A booking with a loan car cannot be received with an empty handover — the
     * service rejects it, and rightly so: the licence and dashboard photos are the
     * evidence of what condition the loan car left in. Posting first and showing that
     * rejection is a dead end, because the form it asks for lives on another screen. So
     * the card opens the SAME VehicleReceivedModal the bookings page uses.
     */
    const handleVehicleReceived = async () => {
        if (job.bookingHasLoanCar) {
            setShowHandoverModal(true);
            return;
        }
        const result = await dispatch(markJobVehicleReceived({ jobUuid: job.uuid }));
        if (markJobVehicleReceived.fulfilled.match(result)) onRefreshJob?.();
    };

    const handleHandoverConfirm = async ({ loanCarDetails }) => {
        const result = await dispatch(markJobVehicleReceived({ jobUuid: job.uuid, loanCarDetails }));
        if (markJobVehicleReceived.fulfilled.match(result)) {
            setShowHandoverModal(false);
            onRefreshJob?.();
        }
    };

    // Header status picker — clicking the status pill in the header opens a
    // dropdown that lets staff change the job's lifecycle state directly.
    // Used to walk a completed job back to in-progress, or skip a created
    // job straight to approved without going through the dedicated workflow
    // buttons. Goes through updateJobStatus which hits PATCH /jobs/{uuid}/status
    // — the dedicated lifecycle endpoints (start/pause/resume/complete) remain
    // available via the footer buttons and continue to be the preferred path
    // for the happy flow because they also stamp timestamps and run side
    // effects (vehicle handover emails, etc.). The dropdown is the escape
    // hatch for corrections.
    const [showStatusMenu, setShowStatusMenu] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const statusMenuRef = useRef(null);

    // Close the menu when clicking outside. Using mousedown rather than click
    // so the menu dismisses before any click inside fires, matching the
    // behaviour of native select dropdowns.
    useEffect(() => {
        if (!showStatusMenu) return undefined;
        const handler = (e) => {
            if (statusMenuRef.current && !statusMenuRef.current.contains(e.target)) {
                setShowStatusMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showStatusMenu]);

    const handleChangeStatus = async (nextStatus) => {
        if (!job.uuid || nextStatus === job.status || statusUpdating) {
            setShowStatusMenu(false);
            return;
        }
        setShowStatusMenu(false);
        setStatusUpdating(true);
        try {
            await dispatch(updateJobStatus({ uuid: job.uuid, status: nextStatus })).unwrap();
        } catch {
            // operationError surfaces via the slice's toast / banner
        } finally {
            setStatusUpdating(false);
        }
    };

    const handleRemove = (uuid) => { setPendingRemoveUuid(uuid); };
    const handleConfirmRemove = () => {
        const uuid = pendingRemoveUuid;
        setPendingRemoveUuid(null);
        if (uuid) onRemoveWorkItem(uuid);
    };
    const handleOpenEdit = (item) => { setEditingItem(item); setShowEditModal(true); };

    // Bulk work-item actions (Start All / Complete All).
    // Both are confirm-gated because Complete All writes an OK value into every
    // OUT measurement slot — destructive in the sense that it overwrites real
    // technician work-in-progress data if the wrong button is tapped. Keeping
    // Start All confirm-gated too for symmetry; the prompt is also a useful
    // "did you mean to do this?" check on a bigger job with many items.
    const [bulkConfirm, setBulkConfirm] = useState(null); // 'start' | 'complete' | null
    const [bulkBusy, setBulkBusy] = useState(false);

    // Counts that drive button enablement + the confirm-dialog summary.
    // Start All / Complete All are hidden whenever ANY work item is still
    // awaiting customer approval — sending the customer a quote item then
    // silently starting/completing work on it would let workshop activity
    // outrun the approval flow. Two signals count as "awaiting":
    //   1. confirmationRequired + customerConfirmed null/undefined
    //      → per-item approval request is in flight or never sent
    //   2. status === 'AWAITING_CONFIRMATION'
    //      → backend has flagged the item as blocked on confirmation
    const pendingCount = (job.workItems || []).filter(wi => wi.status === 'PENDING').length;
    const nonTerminalCount = (job.workItems || [])
        .filter(wi => wi.status !== 'COMPLETED' && wi.status !== 'DECLINED').length;
    const hasAwaitingApproval = (job.workItems || []).some(wi =>
        wi.status === 'AWAITING_CONFIRMATION'
        || (wi.confirmationRequired && (wi.customerConfirmed === null || wi.customerConfirmed === undefined))
    );

    const handleStartAll = async () => {
        setBulkBusy(true);
        try {
            await dispatch(startAllWorkItems({ jobUuid: job.uuid })).unwrap();
        } catch {
            // operationError surfaces via the slice's toast
        } finally {
            setBulkBusy(false);
            setBulkConfirm(null);
        }
    };

    const handleCompleteAll = async () => {
        setBulkBusy(true);
        try {
            await dispatch(completeAllWorkItems({ jobUuid: job.uuid })).unwrap();
        } catch {
            // operationError surfaces via the slice's toast
        } finally {
            setBulkBusy(false);
            setBulkConfirm(null);
        }
    };

    const openDeleteDialog = () => { setDeleteJobInput(''); setShowDeleteJobDialog(true); };
    const handleConfirmDeleteJob = () => {
        if (deleteJobInput.trim() !== job.jobNumber) return;
        // The modal will be closed by the slice's purgeJob.fulfilled reducer
        // (it flips showViewModal off), so no onClose() here.
        dispatch(purgeJob(job.uuid));
        setShowDeleteJobDialog(false);
    };

    // Stable callback so the Escape stack registers a consistent identity
    // across renders. Activates the inline delete-job dialog's own Escape
    // handler — it sits ON TOP of the JobDetailModal handler in the stack,
    // so Esc dismisses just the inner dialog without closing the parent.
    const closeDeleteJobDialog = useCallback(
        () => setShowDeleteJobDialog(false),
        [],
    );
    useEscapeToClose(showDeleteJobDialog, closeDeleteJobDialog);

    return (
        <>
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl">

                    {/* Header */}
                    <div className="px-6 py-4 border-b flex items-start justify-between shrink-0">
                        <div className="flex items-center gap-3">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="flex items-center gap-1 px-2.5 py-1.5 -ml-1 rounded-lg text-sm font-medium text-violet-700 hover:bg-violet-50 transition-colors shrink-0"
                                    title={backLabel || 'Back'}
                                >
                                    <ChevronLeft size={16} />
                                    <span className="hidden sm:inline">{backLabel || 'Back'}</span>
                                </button>
                            )}
                            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center shrink-0"><Wrench size={18} className="text-violet-600" /></div>
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h2 className="text-xl font-bold text-gray-900">{job.jobNumber}</h2>
                                    {/* Assigned-to surfaced into the job-id row at the same visual
                                        weight as the job number — workshop staff identify each job
                                        primarily by who's on it, so promoting this out of the info
                                        grid below makes the assignment scannable at the top of the
                                        modal. Rendered as a separator + name so it reads as a
                                        continuation of the title, not a tag. The Assign button in
                                        the footer remains the way to change it. */}
                                    {job.assignedStaffName ? (
                                        <>
                                            <span className="text-xl font-bold text-gray-300" aria-hidden="true">·</span>
                                            <span className="text-xl font-bold text-violet-700 truncate" title={`Assigned to ${job.assignedStaffName}`}>{job.assignedStaffName}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-xl font-bold text-gray-300" aria-hidden="true">·</span>
                                            <span className="text-xl font-bold text-gray-300 italic">Unassigned</span>
                                        </>
                                    )}
                                    {/* Static status pill — the interactive status-change dropdown
                                        has been hidden for now. Status changes happen via the
                                        dedicated lifecycle buttons (Start / Pause / Resume /
                                        Complete) in the footer, which also stamp timestamps and
                                        run side effects. To re-enable the free-form dropdown,
                                        restore the previous block from VCS — the underlying
                                        state (showStatusMenu, statusUpdating, statusMenuRef,
                                        handleChangeStatus, JOB_STATUS_OPTIONS) is kept in place. */}
                                    <span
                                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-600'}`}
                                    >
                                        {job.status?.replace(/_/g, ' ')}
                                    </span>
                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${PRIORITY_COLORS[job.priority] || ''}`}>{job.priority}</span>
                                </div>
                                <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                                    {/* Vehicle description may arrive with a leading "0 " when the
                                        backend has no year for the car (year=0). Strip it so the
                                        subtitle reads "Honda Civic" rather than "0 Honda Civic".
                                        Customer name was previously prefixed here but has been
                                        dropped — it's still shown in the info-grid Customer tile
                                        below, and removing it here keeps the focus on the
                                        vehicle, which is what the technician identifies the job by. */}
                                    <span>{(job.vehicleDescription || '').replace(/^0\s+/, '') || job.vehicleRego}</span>
                                    <TransmissionBadge value={job.vehicleTransmission} variant="pill" />
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl shrink-0"><X size={20} /></button>
                    </div>

                    {/* Body */}
                    <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
                        {/*
                         * Inline error banner — shows the most recent failure
                         * from any action dispatched while this modal is open
                         * (Start Job, Pause, Resume, Add Work Item, Assign
                         * Resource, etc.). The backend's BadRequest messages
                         * are intentionally specific (e.g. "Cannot start job:
                         * booking must be marked VEHICLE RECEIVED first"), so
                         * showing them verbatim is more useful than a toast
                         * that scrolls off-screen.
                         *
                         * Dismissable — the slice's clearOperationStatus is
                         * also auto-called by JobsPage's existing toast on
                         * fulfilled actions, so the banner clears naturally
                         * after the next successful op.
                         */}
                        {operationError && (
                            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
                                <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                                <span className="flex-1">{operationError}</span>
                                {onClearError && (
                                    <button
                                        onClick={onClearError}
                                        className="shrink-0 p-0.5 text-red-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
                                        title="Dismiss"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        )}
                        {/* A WRITE on the open card — saving an item, recording a
                            reading — says so in a line and leaves the card standing. */}
                        {operationLoading && !detailLoading && <div className="flex items-center gap-2 text-sm text-violet-600 bg-violet-50 px-3 py-2 rounded-lg"><RefreshCw size={14} className="animate-spin" /> Loading…</div>}

                        {/* 🔑 The card was handed a name and a number by the board and
                            fetches the rest. Until that answers, the shapes stand in for
                            the tiles and the work items — a job with four items used to
                            open looking like a job with none. */}
                        {detailLoading && <JobDetailLoading />}

                        {/* Info grid — compact tiles, dense layout.
                            Labels are micro-caps (uppercase 10px) so they read clearly
                            at small size; values stay at text-sm for legibility.
                            Grid expands to 4 cols on wider viewports so the full set of
                            tiles fits in fewer rows. */}
                        {/* 🔑 The invoice this job produced, and the way to it.
                            invoiceUuid is the SERVER's answer — resolved through the
                            authoritative foreign key — so it is never inferred from the
                            job's status, and a job that was never invoiced shows
                            nothing rather than a dead control. */}
                        {!detailLoading && job.invoiceNumber && (
                            <div className="flex items-center gap-2">
                                <RecordCrossLink
                                    uuid={job.invoiceUuid}
                                    label={job.invoiceNumber}
                                    detail={invoiceDetailText(job)}
                                    icon={FileText}
                                    onOpen={openInvoice}
                                    loading={openingInvoice}
                                    title={`Open invoice ${job.invoiceNumber}`}
                                />
                            </div>
                        )}

                        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 ${detailLoading ? 'hidden' : ''}`}>
                            <CustomerContactCard job={job} />
                            {job.totalEstimatedCost > 0 && <div className="bg-gray-50 rounded-lg px-2.5 py-1.5"><p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Est. Cost</p><p className="text-sm font-semibold text-gray-900 leading-tight">${Number(job.totalEstimatedCost).toFixed(2)}</p></div>}
                            {/* Odometer occupies the position where the Assigned To tile used to live —
                                Assigned To has been promoted into the job-id row in the header above, so
                                this slot frees up for the inline-editable odometer reading. Keeps the
                                info grid the same shape as before (no empty slot, no reflow on wide
                                viewports). */}
                            <OdometerInTile job={job} dispatch={dispatch} />
                            {job.scheduledStartDate && <div className="bg-gray-50 rounded-lg px-2.5 py-1.5"><p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Scheduled</p><p className="text-sm font-semibold text-gray-900 leading-tight">{job.scheduledStartDate}</p></div>}
                        </div>

                        {(() => {
                            // job.description is now a structured JobDescription value object:
                            //   { serviceTypes: [{ serviceName, description }, ...], freeText }
                            // Legacy / free-text-only rows render the freeText paragraph.
                            // When serviceTypes is non-empty we render a numbered list so the
                            // technician sees each customer complaint as its own line —
                            // e.g. "1. SUSPENSION: Suspension leaky".
                            //
                            // String fallback covers any stale client cache that still holds a
                            // plain-string description from before the migration.
                            const desc = job.description;
                            if (!desc) return null;

                            if (typeof desc === 'string') {
                                return (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Description</p>
                                        <p className="text-sm text-gray-700 bg-gray-50 rounded-xl px-4 py-3 leading-relaxed">{desc}</p>
                                    </div>
                                );
                            }

                            const serviceTypes = Array.isArray(desc.serviceTypes) ? desc.serviceTypes : [];
                            const freeText = desc.freeText;

                            if (serviceTypes.length === 0 && !freeText) return null;

                            return (
                                <div>
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Description</p>
                                    <div className="text-sm text-gray-700 bg-gray-50 rounded-xl px-4 py-3 leading-relaxed">
                                        {serviceTypes.length > 0 && (
                                            // Compact one-line-per-row layout — chip on the left,
                                            // description flowing after it. Tighter than the table
                                            // form and reads naturally when descriptions are short.
                                            <ul className="space-y-1">
                                                {serviceTypes.map((entry, idx) => (
                                                    <li key={idx} className="flex items-baseline gap-2 leading-snug">
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold tracking-wide bg-violet-50 text-violet-700 shrink-0">
                                                            {entry.serviceName}
                                                        </span>
                                                        {entry.description && (
                                                            <span className="text-sm text-gray-700 break-words">
                                                                {entry.description}
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {freeText && (
                                            <p className={serviceTypes.length > 0 ? 'mt-2 text-gray-600 italic' : ''}>{freeText}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Tabs */}
                        <div className="flex gap-1 border-b border-gray-200 -mx-6 px-6 shrink-0">
                            {(() => {
                                // The Customer Approvals tab gets a count badge representing
                                // the number of items eligible for approval (not yet decided),
                                // OR a single-dot indicator when a previous request is in flight
                                // and awaiting customer response — matching how the BatchApprovalCard
                                // itself surfaces those two states. PROFILE-mode jobs use the
                                // simpler single job-level approval, so we treat any in-flight
                                // SENT request as the badge for both modes.
                                //
                                // Fallback: when nothing is eligible AND nothing is in flight,
                                // surface the past-request count so the tab still reflects the
                                // presence of the audit-trail history list rendered inside.
                                // Match BatchApprovalCard.eligibleItems exactly:
                                // undecided + has a dollar value. See isApprovable
                                // at the top of the file. Keeps the badge count and
                                // the picker count in sync — surprising mismatches
                                // here erode trust ("badge says 5 but only 4 in list").
                                const eligibleApprovalCount = (job.workItems || []).filter(isApprovable).length;
                                const approvalAwaiting = job.lastApprovalStatus === 'SENT';
                                const historyCount = Array.isArray(job.approvalHistory) ? job.approvalHistory.length : 0;
                                const approvalsBadge = approvalAwaiting
                                    ? null
                                    : (eligibleApprovalCount > 0 ? eligibleApprovalCount : historyCount);
                                const tabs = [
                                    { id: 'details',   label: 'Work Items',         count: job.workItems?.length || 0 },
                                    // "Notes & Photos", because the photo is often the
                                    // point: the worn part the customer was shown, the
                                    // damage that was there on arrival. Staff looking for
                                    // where to put a picture did not think to look under
                                    // "Notes".
                                    { id: 'notes',     label: 'Notes & Photos',     count: job.notes?.length || 0 },
                                    { id: 'approvals', label: 'Customer Approvals', count: approvalsBadge, dot: approvalAwaiting },
                                    // Resources moved out of the Work Items tab into its own dedicated
                                    // tab — keeps the Work Items list focused on the work itself, and
                                    // puts resource assignment alongside the other lifecycle concerns.
                                    // Placed last so it sits at the end of the tab row.
                                    { id: 'resources', label: 'Resources',          count: jobResources?.length || 0 },
                                ];
                                return tabs.map(tab => (
                                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === tab.id ? 'border-violet-600 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                                        {tab.label}
                                        {tab.dot && <span className="w-2 h-2 rounded-full bg-amber-500" title="Awaiting customer response" />}
                                        {tab.count > 0 && <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${activeTab === tab.id ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'}`}>{tab.count}</span>}
                                    </button>
                                ));
                            })()}
                        </div>

                        {/* Work Items tab */}
                        {activeTab === 'details' && !detailLoading && (
                            <div className="space-y-6">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Work Items {job.workItems?.length > 0 && `(${job.workItems.length})`}</p>
                                        <div className="flex items-center gap-2">
                                            {/* Bulk actions — only render when there's something to act on
                                                so the header stays uncluttered for QUOTED / fully-completed
                                                jobs. Start All shows when there's at least one PENDING item;
                                                Complete All shows when at least one item is still non-terminal.
                                                Both go through ConfirmDialog because Complete All in particular
                                                writes an OK value into every OUT measurement on every item it
                                                touches — easy to fat-finger, painful to undo. */}
                                            {isWorkItemEditable && pendingCount > 0 && !hasAwaitingApproval && (
                                                <button
                                                    onClick={() => setBulkConfirm('start')}
                                                    disabled={bulkBusy || operationLoading}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-50 disabled:opacity-50 transition-colors"
                                                    title={`Start ${pendingCount} pending work item${pendingCount === 1 ? '' : 's'}`}
                                                >
                                                    <Play size={12} /> Start All
                                                </button>
                                            )}
                                            {isWorkItemEditable && nonTerminalCount > 0 && !hasAwaitingApproval && (
                                                <button
                                                    onClick={() => setBulkConfirm('complete')}
                                                    disabled={bulkBusy || operationLoading}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-300 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-50 disabled:opacity-50 transition-colors"
                                                    title={`Complete ${nonTerminalCount} work item${nonTerminalCount === 1 ? '' : 's'} and auto-fill OUT measurements`}
                                                >
                                                    <CheckCheck size={12} /> Complete All
                                                </button>
                                            )}
                                            {/* ⚠️ The "Import from Repco" button was removed here on request.
                                                It was shown only to MECHANIC_WORKSHOP, so this was its only
                                                way in — RepcoImportModal, the slice and the whole
                                                LLM-extraction pipeline behind it are untouched and still
                                                work, they simply have no entry point in the UI. Put the
                                                button back to restore it; nothing else needs changing. */}
                                            {isEditable && <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700 transition-colors"><Plus size={12} /> Add Work Item</button>}
                                        </div>
                                    </div>
                                    {(!job.workItems || job.workItems.length === 0)
                                        ? <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl"><Wrench size={20} className="mx-auto mb-2 opacity-40" />No work items yet</div>
                                        : <div className="space-y-3">
                                            {/* Priced items first, then $0 inspection/check items.
                                                Stable sort (Array.prototype.sort is stable per ES2019),
                                                so within each group the backend's sortOrder is preserved.
                                                Spread first to avoid mutating the Redux state slice. */}
                                            {[...job.workItems]
                                                .sort((a, b) => (workItemTotal(b) > 0 ? 1 : 0) - (workItemTotal(a) > 0 ? 1 : 0))
                                                .map(item => (
                                                    <WorkItemCard key={item.uuid} item={item} job={job} isEditable={isEditable} isWorkItemEditable={isWorkItemEditable} operationLoading={operationLoading} dispatch={dispatch}
                                                                  onEdit={() => handleOpenEdit(item)} onRemove={() => handleRemove(item.uuid)} />
                                                ))}
                                        </div>
                                    }
                                </div>
                                {/* ResourcePanel was previously rendered here. It now lives in its own
                                    Resources tab (the last tab in the row) — see activeTab === 'resources'
                                    block below. */}
                            </div>
                        )}

                        {/* Customer Approvals tab — was previously rendered as cards inside
                            the Work Items tab. Splitting it out reduces noise on the Work Items
                            list and gives the approval flow its own dedicated surface, which
                            matters when the tech is bundling several items into a single request. */}
                        {activeTab === 'approvals' && (
                            <div className="space-y-6">
                                {/* Job-level customer approval — interactive card for PROFILE mode */}
                                {job.approvalMode === 'PROFILE' && (
                                    <JobApprovalCard job={job} isEditable={isEditable} operationLoading={operationLoading} dispatch={dispatch} />
                                )}
                                {/* Batch approval — for non-PROFILE jobs, lets mechanic bundle multiple ad-hoc items into one request.
                                    NOT for a pending quote: the quote goes to the customer whole, with no item
                                    selection, and its Send button is in the footer. Two paths to one email would
                                    let staff think they had sent a subset when the server always sends every
                                    priced item. */}
                                {job.approvalMode !== 'PROFILE' && !isPendingQuote && (
                                    <BatchApprovalCard job={job} isEditable={isEditable} operationLoading={operationLoading} dispatch={dispatch} />
                                )}
                                {/* Audit-trail history — every approval request ever sent for this
                                    job, with its outcome. Stays visible after items resolve so the
                                    workshop has a permanent record of what was asked and answered.
                                    The per-work-item CONFIRMED badge on the Work Items tab continues
                                    to be the at-a-glance per-item signal. */}
                                {/* WHAT was decided, then WHEN it was asked. The first
                                    answers "did they agree to this?"; the second is the
                                    audit trail of requests behind it. */}
                                <CustomerDecisionList workItems={job.workItems} />
                                <ApprovalHistoryList history={job.approvalHistory} />
                                {/* Empty-state fallback — BatchApprovalCard returns null when there's
                                    nothing eligible AND no in-flight request. We also hide this when
                                    history is non-empty: the history panel above already conveys that
                                    there has been approval activity, so an "empty" message would
                                    contradict it.

                                    Note: zero-cost items never participate in approval (see
                                    isApprovable), so they're excluded from the "all decided" check —
                                    otherwise jobs with any check/inspection lines would never show
                                    the empty state even after every priced item is decided. */}
                                {job.approvalMode !== 'PROFILE'
                                    && (job.workItems || []).filter(wi => workItemTotal(wi) > 0).every(wi => wi.customerConfirmed === true || wi.customerConfirmed === false)
                                    && job.lastApprovalStatus !== 'SENT'
                                    && (!Array.isArray(job.approvalHistory) || job.approvalHistory.length === 0)
                                    && (
                                        <div className="text-center py-10 text-gray-400 text-sm bg-gray-50 rounded-xl">
                                            <Send size={20} className="mx-auto mb-2 opacity-40" />
                                            No items awaiting customer approval
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* Notes tab — measurement recording has moved to the
                            Work Items tab (big IN / OUT buttons on each card),
                            so the Notes tab is now purely note-taking. */}
                        {activeTab === 'notes' && (
                            <JobNotesTab
                                job={job}
                                onNoteAdded={onAddNote}
                                onRefreshJob={onRefreshJob}
                                operationLoading={operationLoading}
                            />
                        )}

                        {/* Resources tab — promoted out of the Work Items tab into its own
                            dedicated surface. Kept as the last tab in the row so the natural
                            left-to-right read order stays Work Items → Notes → Customer
                            Approvals → Resources, matching the lifecycle order on the tablet
                            view. The ResourcePanel itself is unchanged — same assign / unassign
                            handlers wired through from JobsPage. */}
                        {activeTab === 'resources' && (
                            isEditable
                                ? <ResourcePanel jobResources={jobResources} availableResources={availableResources} resourcesLoading={resourcesLoading} operationLoading={operationLoading}
                                                 resourceTypeFilter={resourceTypeFilter} onFilterChange={onResourceTypeFilter} onAssign={onAssignResource} onUnassign={onUnassignResource} />
                                : <div className="text-center py-10 text-gray-400 text-sm bg-gray-50 rounded-xl"><Boxes size={20} className="mx-auto mb-2 opacity-40" />Resource assignment is not available for this job</div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex items-center gap-2 shrink-0 flex-wrap">
                        {/*
                         * Action buttons no longer auto-close the modal:
                         *   • Start / Pause / Resume — simple state transitions.
                         *     The job's status badge in the header re-renders
                         *     when the slice's fulfilled handler updates
                         *     selectedJob, so staff see the new state without
                         *     losing their place.
                         *   • Complete / Vehicle Collected / Assign — open
                         *     dedicated confirm/picker modals that stack ON TOP
                         *     of this one. Dismissing the inner dialog returns
                         *     the user here rather than to the calendar / kanban,
                         *     which matters when this modal was opened from the
                         *     Bookings page via "View Job".
                         */}
                        {/* A quote awaiting the customer is not startable work: nobody
                            has authorised it and the car is not here. Its one action is
                            sending it — which goes to the customer as a whole, with no
                            item selection, because the quote IS the thing being
                            authorised. Once they answer, the quote becomes a CREATED job
                            and picks up the ordinary buttons below. */}
                        {/* A job with no appointment: an approved quote, or any job
                            created standalone. Offered here rather than sending somebody
                            to the bookings page to make a second, unrelated booking. */}
                        {needsBooking && (
                            <button onClick={() => setShowBookingDialog(true)} disabled={operationLoading}
                                    className="flex items-center gap-2 px-4 py-2 bg-white border border-indigo-300 text-indigo-700 rounded-xl text-sm font-medium hover:bg-indigo-50 disabled:opacity-50">
                                <CalendarPlus size={15} /> Book in
                            </button>
                        )}
                        {/* Booked in, car not here yet. The person holding the keys says
                            so from the job rather than finding the booking. */}
                        {awaitingVehicle && (
                            <button onClick={handleVehicleReceived} disabled={operationLoading}
                                    className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-xl text-sm font-medium hover:bg-sky-700 disabled:opacity-50">
                                <Car size={15} /> Vehicle Received
                            </button>
                        )}
                        {isPendingQuote
                            ? (onSendQuote && <button onClick={() => onSendQuote(job)}
                                    // 🔴 ABSENT is not EMPTY: customerEmail was added to
                                    // JobResponse after this shipped, so an un-restarted
                                    // server sends no such field. Disabling on undefined
                                    // would block sending a quote whose customer has an
                                    // email perfectly well.
                                    disabled={operationLoading
                                        || (Object.prototype.hasOwnProperty.call(job, 'customerEmail')
                                            && !job.customerEmail)}
                                    // 🔑 Approval IS an email. The server refuses without
                                    // one — "customer has no email address on file" — so
                                    // pressing this was the only way to find out. The
                                    // customer card above adds it in place.
                                    title={Object.prototype.hasOwnProperty.call(job, 'customerEmail')
                                            && !job.customerEmail
                                        ? 'This customer has no email address — add one on the customer card above'
                                        : 'Send every priced item to the customer'}
                                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-50">
                                    <Send size={15} /> Send for approval</button>)
                            : ['CREATED', 'QUOTED', 'APPROVED'].includes(job.status) && <button onClick={() => onStart(job)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700"><Play size={15} /> Start Job</button>}
                        {job.status === 'IN_PROGRESS' && <>
                            <button onClick={() => onPause(job)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600"><Pause size={15} /> Pause</button>
                            <button onClick={() => onOpenComplete(job)} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700"><CheckCircle size={15} /> Complete</button>
                        </>}
                        {job.status === 'PAUSED' && <button onClick={() => onResume(job)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700"><Play size={15} /> Resume</button>}
                        {['COMPLETED', 'INVOICED'].includes(job.status) && <button onClick={() => onOpenVehicleCollect(job)} className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-xl text-sm font-medium hover:bg-sky-700"><Car size={15} /> Vehicle Collected</button>}
                        {!isPendingQuote && <button onClick={() => onOpenAssign(job)} className="flex items-center gap-2 px-4 py-2 bg-white border rounded-xl text-sm font-medium hover:bg-gray-50 text-gray-700"><UserPlus size={15} /> Assign</button>}
                        {/* Delete job — irreversible. Cascades through booking + invoice
                            on the backend. Sits to the left of Close, separated visually
                            by ml-auto so it doesn't bunch up with the workflow buttons.
                            The action goes through a type-to-confirm dialog below. */}
                        <button
                            onClick={openDeleteDialog}
                            disabled={operationLoading}
                            className="ml-auto flex items-center gap-2 px-4 py-2 bg-white border border-red-200 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50 disabled:opacity-50"
                            title="Permanently delete this job, its booking and invoice"
                        >
                            <Trash2 size={15} /> Delete
                        </button>
                        <button onClick={onClose} className="px-4 py-2 border rounded-xl text-sm text-gray-500 hover:bg-gray-100">Close</button>
                    </div>
                </div>
            </div>

            {/* Add Work Item */}
            {/* Loan car handover — the same form the bookings page uses, so the two
                paths collect and post identical evidence. Only the shell fields it
                displays come from the job. */}
            {showHandoverModal && (
                <VehicleReceivedModal
                    booking={{ bookingCode: job.bookingCode, customerName: job.customerName }}
                    imageCategories={loanCarImageCategories}
                    onConfirm={handleHandoverConfirm}
                    onClose={() => setShowHandoverModal(false)}
                    loading={operationLoading}
                    error={operationError}
                />
            )}

            {/* Book in — a date, and optionally a time. Deliberately small: the
                customer and vehicle are already settled on the job, so the only thing
                missing is when. */}
            {showBookingDialog && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
                     onClick={() => setShowBookingDialog(false)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
                         onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-base font-semibold text-gray-900">Book this job in</h3>
                        <p className="mt-1 text-xs text-gray-500">
                            {job.customerName} · {job.vehicleDescription}
                        </p>

                        <label className="mt-4 block">
                            <span className="text-xs font-medium text-gray-600">Date</span>
                            <input type="date" value={bookingDate}
                                   onChange={(e) => setBookingDate(e.target.value)}
                                   className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500" />
                        </label>

                        <label className="mt-3 block">
                            <span className="text-xs font-medium text-gray-600">
                                Time <span className="font-normal text-gray-400">optional</span>
                            </span>
                            <input type="time" value={bookingTime}
                                   onChange={(e) => setBookingTime(e.target.value)}
                                   className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500" />
                        </label>

                        {bookingError && (
                            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                                {bookingError}
                            </p>
                        )}

                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => setShowBookingDialog(false)}
                                    className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
                                Cancel
                            </button>
                            <button onClick={handleBookIn} disabled={!bookingDate || operationLoading}
                                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
                                Book in
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showAddModal && (
                <AddWorkItemModal job={job} mode="add" onClose={() => setShowAddModal(false)}
                                  onSubmit={(payload) => { onAddWorkItem(payload); setShowAddModal(false); }}
                                  loading={operationLoading} workCategories={workCategories} categoryTypes={categoryTypes} />
            )}

            {/* Edit Work Item */}
            {showEditModal && editingItem && (
                <AddWorkItemModal job={job} mode="edit"
                                  onClose={() => { setShowEditModal(false); setEditingItem(null); }}
                                  onSubmit={(payload) => { onEditWorkItem(payload, editingItem.uuid); setShowEditModal(false); setEditingItem(null); }}
                                  loading={operationLoading} workCategories={workCategories} categoryTypes={categoryTypes}
                                  initialValues={{ description: editingItem.description ?? '', detailNotes: editingItem.detailNotes ?? '', workCategory: editingItem.workCategory ?? '', confirmationRequired: editingItem.confirmationRequired ?? false, confirmationMessage: editingItem.confirmationMessage ?? '', status: editingItem.status ?? '', costDetail: editingItem.costDetail ?? null }} />
            )}

            {/* In-app confirmation for removing a work item — replaces the
                old window.confirm so the prompt is styled consistently. */}
            <ConfirmDialog
                open={pendingRemoveUuid != null}
                title="Remove work item?"
                message="This will remove the work item from the job. This action cannot be undone."
                confirmLabel="Remove"
                cancelLabel="Cancel"
                variant="danger"
                onConfirm={handleConfirmRemove}
                onCancel={() => setPendingRemoveUuid(null)}
            />

            {/* Bulk Start All / Complete All confirmation. Same ConfirmDialog,
                two messages — Complete All gets the extra "will fill all OUT
                measurements with OK" line because that's the bit a technician
                might not expect on first encounter. */}
            <ConfirmDialog
                open={bulkConfirm != null}
                title={bulkConfirm === 'complete' ? 'Complete all work items?' : 'Start all work items?'}
                message={
                    bulkConfirm === 'complete'
                        ? `This will mark ${nonTerminalCount} work item${nonTerminalCount === 1 ? '' : 's'} as completed and auto-fill every OUT measurement with "OK". Items already completed, declined, or awaiting customer confirmation will be skipped.`
                        : `This will move ${pendingCount} pending work item${pendingCount === 1 ? '' : 's'} into In Progress.`
                }
                confirmLabel={bulkConfirm === 'complete' ? 'Complete All' : 'Start All'}
                cancelLabel="Cancel"
                variant={bulkConfirm === 'complete' ? 'primary' : 'primary'}
                onConfirm={bulkConfirm === 'complete' ? handleCompleteAll : handleStartAll}
                onCancel={() => setBulkConfirm(null)}
            />

            {/* Delete-job type-to-confirm dialog.
                Backed by DELETE /api/jobs/{uuid}/purge — irreversibly removes
                the job and cascades through its booking and invoice. We require
                the user to type the exact job number to enable the destructive
                button, matching the GitHub/Stripe pattern for cascading deletes. */}
            {showDeleteJobDialog && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
                        <div className="px-6 py-4 border-b flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                                <AlertTriangle size={20} className="text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">Delete job?</h2>
                                <p className="text-xs text-gray-500 mt-0.5">This action is permanent and cannot be undone.</p>
                            </div>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 space-y-1">
                                <p className="font-semibold">The following will be permanently deleted:</p>
                                <ul className="list-disc pl-5 space-y-0.5">
                                    <li>Job <span className="font-mono">{job.jobNumber}</span> and all its work items, notes and observations</li>
                                    <li>Its booking (if any)</li>
                                    <li>Its invoice and line items (if any)</li>
                                </ul>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type <span className="font-mono font-semibold text-gray-900">{job.jobNumber}</span> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={deleteJobInput}
                                    onChange={(e) => setDeleteJobInput(e.target.value)}
                                    placeholder={job.jobNumber}
                                    className="w-full border rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                    autoFocus
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowDeleteJobDialog(false)}
                                    className="flex-1 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmDeleteJob}
                                    disabled={operationLoading || deleteJobInput.trim() !== job.jobNumber}
                                    className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold"
                                >
                                    {operationLoading ? 'Deleting…' : 'Delete job'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Repco SmartQuote import — opens from the Work Items tab button.
                The slice owns the modal's visibility; this just renders it.
                onRefreshJob is forwarded so the new work items appear on the
                tab immediately after commit without an extra dispatch path. */}
            <RepcoImportModal job={job} onRefreshJob={onRefreshJob} />
        </>
    );
};

// isApprovable is exported alongside the components so MobileJobsDashboard's
// approvals tab badge counts exactly what BatchApprovalCard lists. A local copy
// there would be one more place for the zero-cost-item rule to drift.
export { WorkItemCard, WorkItemMeasurementsPanel, ObservationRow, AddMeasurementPicker, AddWorkItemModal, JobApprovalCard, BatchApprovalCard, ApprovalHistoryList, isApprovable };
export default JobDetailModal;